// src/components/Worklist.tsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import {
  FaFilter,
  FaTimes,
  FaStar,
  FaChevronDown,
  FaSearch,
  FaBookmark,
  FaRegBookmark,
  FaSortAmountDown,
  FaExclamationTriangle,
  FaDownload,
} from "../utils/icons";
import { WorklistListPicker, WorklistListOption, type ListEntry } from "./WorklistListPicker";
import { CaseReportDownloadButton } from "./CaseReportDownloadButton";
import { useCollections, useStudyCollectionIds } from "../hooks/useCollections";
import { isAdminAtom, userTypeAtom } from "../atoms/authAtoms";
import CaseViewer from "./CaseViewer";
import { useNeuroWorklist } from "@/hooks/useNeuroWorklist";
import { useTriageWorklists } from "@/hooks/useTriageSiteWorklist";
import { useDevicePanels } from "@/hooks/useDevicePanel";
import { panelUrl } from "@/types/devicePanel";
import { useTriageWorklist } from "@/hooks/useTriageWorklist";
import { isNeuroModel, neuroFindingLabel } from "@/types/neuroReview";
import { initializeCornerstone } from "../utils/dicomLoader";
import { useAuth } from "../context/AuthContext";
import { StudyData } from "./types";
import { formatConditionDisplay, formatFindingName, middleEllipsis } from "../utils/textFormatters";
import { getFindingSummary, parseBoldSegments } from "../utils/findingSummary";
import { validateDetectionCascade } from "../utils/detectionCascade";
import { useInView } from "../hooks/useInView";
import { type FindingsByOpLevel } from "../utils/reportGenerator";
import { type OpLevel, OP_LEVELS, getDetectedThreshold } from "../config/operatingPoints";
import { compareUrgency, priorityLabel } from "../utils/findingPriority";
import {
  caseSeverity,
  severityRank,
  compareFindingSeverity,
  tierCaseSeverity,
  type Severity,
  type SeveritySource,
} from "../config/findingSeverity";
import { isReporterStudy, reporterMissedCount } from "../utils/reporterStudy";
import { isPredictedNoise } from "../hooks/useSurfacerCandidates";
import { useAllSurfacerDecisions, type AllDecisionsMap } from "@/hooks/useSurfacerDecisions";
import { useOnlyUnjudged } from "../hooks/useOnlyUnjudged";
import { useSurfacerReviewStates } from "../hooks/useSurfacerReviewState";
import { unjudgedFindings, unjudgedTally } from "../utils/surfacerNextCase";
import { hiddenToggleLabels } from "../utils/surfacerHiddenLabels";
import {
  useAllSkippedStudies,
  useSkipCase,
  SKIP_REASON_LABEL,
  type SkippedMap,
  type SkipReason,
} from "@/hooks/useSkippedStudies";
import { SkipReasonMenu } from "./SkipReasonMenu";
import {
  useCohorts,
  useReaderCohort,
  useReaderCohorts,
  useReviewCohort,
  fetchModelPrediction,
  fetchReportState,
  useReportState,
  useSavedCases,
  saveCase,
  deleteSavedCase,
  type DispositionOverlay,
  type DispositionVerdict,
  type ReaderStudyItem,
  type ReviewerStudyItem,
} from "../hooks/useStudyQueries";
import { readerCaseId, readerCaseViewerPath } from "../utils/readerCaseId";
import { useQueryClient } from "@tanstack/react-query";
import { filterActiveCohorts } from "../utils/filterCohorts";
import { isModelRunning, isModelFailed } from "../utils/inferenceStatus";
import { decodeToken } from "../utils/tokenUtils";
import {
  selectedDeviceIdAtom,
  selectedFindingsAtom,
  findingFilterModeAtom,
  previewStudyIdAtom,
  searchQueryAtom,
  sortModeAtom,
  type SortMode,
} from "../atoms/filterAtoms";
import { RadioGroup, RadioGroupItem, Checkbox, Label } from "@/design-system/components/react";
import { PreviewGallery } from "./PreviewGallery";
import { RetroAgreementStage } from "./surfacer/RetroAgreementStage";
import { ComplexityBars } from "./ComplexityBars";
import { complexityLevel } from "../utils/complexity";
import { useStudyBbox, useStudyMetadata, useModelPrediction } from "../hooks/useStudyQueries";
import { examLabelFromMetadata } from "../utils/examLabel";
import { StudyNotCanonicalizedError } from "../utils/dicomMetadataLoader";
import { GhostWorklistRows } from "./GhostWorklistRows";
import { isInternalRole } from "../services/roles";
import { fetchReport, useReport } from "../hooks/useReportQuery";
import { downloadCombinedReports, summarizeExportShortfall } from "../utils/reportDownload";
import { makeDownloadableReport } from "../utils/reportDocument";

// Sort modes that get the visible reorder motion: the "prioritization" sorts
// where watching cases slide into rank is meaningful. Default / Fewest findings
// / Alphabetical reorder instantly (no animation). Layout tracking stays on for
// every mode; these just decide whether the position change animates.
const ANIMATED_SORTS = new Set<SortMode>(["severity", "findings_desc", "flagged"]);
const ACCESSION_LOOKUP_FAILED = "accession_lookup_failed";

const EMPTY_FINDINGS_BY_OP: FindingsByOpLevel = {
  high_specificity: [],
  balanced: [],
  sensitivity_biased: [],
  high_sensitivity: [],
};

// Rows rendered from a device's emitted decisions (Neuro, released triage devices)
// instead of research detected_at_* lists. None of the research surfaces apply to
// them: drafted report, preview gallery, severity heuristic, cascade check.
function isDecisionDevice(
  study: { device_id?: string; triageDevice?: boolean } | undefined,
): boolean {
  return !!study && (isNeuroModel(study.device_id) || !!study.triageDevice);
}

function isDecisionItem(item: ReaderStudyItem): boolean {
  return isNeuroModel(item.model) || !!item.triage_device;
}

function worklistFindings(study: StudyData) {
  if (study.neuroSummary) {
    return study.neuroSummary.findings.map((condition) => ({
      organ: "",
      finding: neuroFindingLabel(condition),
    }));
  }
  // Triage flags arrive as the device's customer display names.
  if (study.triageDevice) {
    return (study.triageSummary?.findings ?? []).map((name) => ({ organ: "", finding: name }));
  }
  // Severity is a prototype keyword heuristic; it does not classify Neuro decisions.
  const byOp = study.findingsByOp ?? EMPTY_FINDINGS_BY_OP;
  return OP_LEVELS.flatMap((level) => byOp[level]).sort((a, b) =>
    compareFindingSeverity(`${a.organ}/${a.finding}`, `${b.organ}/${b.finding}`),
  );
}

// Stable empty passing set for rows missing from the study index (e.g. a
// directly-deep-linked study). Shared reference avoids re-rendering CaseRow.
const EMPTY_PASSING_SET: Set<string> = new Set();

// Stable empty surfacer-decisions overlay — avoids studyIndex recompute churn
// while the bulk decisions query is still loading.
const EMPTY_DECISIONS: AllDecisionsMap = {};

// Stable empty skipped overlay — same churn-avoidance as EMPTY_DECISIONS.
const EMPTY_SKIPPED: SkippedMap = {};

const READER_OP_KEYS: Record<OpLevel, keyof ReaderStudyItem> = {
  high_specificity: "detected_at_high_specificity",
  balanced: "detected_at_balanced",
  sensitivity_biased: "detected_at_sensitivity_biased",
  high_sensitivity: "detected_at_high_sensitivity",
};

function buildFindingsByOp(
  item: ReaderStudyItem,
  threshold: OpLevel | "recommended",
): FindingsByOpLevel {
  const result: FindingsByOpLevel = {
    high_specificity: [],
    balanced: [],
    sensitivity_biased: [],
    high_sensitivity: [],
  };
  // Reporter rows expose comparison_counts instead of detected_at_*. We
  // surface those as a separate display path (reporter cards) — return
  // empty here so the cortex/drafter rendering doesn't crash on missing
  // detected_at_* fields.
  if (item.comparison_counts) {
    return result;
  }
  // For cortex device: passing set = `detected_at_recommended` (per-finding
  // recommended_threshold). Each finding is bucketed into its strictest
  // passing OP for badge color, falling back to high_sensitivity if absent.
  const passing =
    threshold === "recommended"
      ? new Set(item.detected_at_recommended ?? [])
      : new Set((item[READER_OP_KEYS[threshold]] as string[] | undefined) ?? []);
  const seen = new Set<string>();
  for (const level of OP_LEVELS) {
    const conditions = (item[READER_OP_KEYS[level]] as string[] | undefined) ?? [];
    for (const condition of conditions) {
      if (seen.has(condition) || !passing.has(condition)) continue;
      seen.add(condition);
      const [organ, finding] = condition.split("/");
      if (!organ || !finding) continue;
      result[level].push({ organ, finding, opLevel: level });
    }
  }
  return result;
}

interface WorklistProps {
  viewMode?: "list" | "case" | "reporter" | "review";
}

const sortStudies = (studies: StudyData[]): StudyData[] => {
  return [...studies].sort((a, b) => {
    // Sort by studyId in ascending order
    return a.studyId.localeCompare(b.studyId);
  });
};

// Confidence bar indicator component

// =============================================================================
// NEW GMAIL-STYLE CASE ROW
// =============================================================================

const StudyMetaLine: React.FC<{ study: StudyData }> = ({ study }) => {
  const parts: string[] = [];
  if (study.modality) parts.push(study.modality);
  if (study.study_description) parts.push(study.study_description);
  if (study.num_series != null) parts.push(`${study.num_series} series`);
  if (parts.length === 0) return null;
  return <div className="text-[10px] text-fg-muted truncate mt-0.5">{parts.join(" \u00b7 ")}</div>;
};

// Source-delivery batch tag (e.g. cal46, ae_abdomen). Renders only when the
// row carries a batch, so cohorts without a batch axis are unaffected.
const BatchTag: React.FC<{ batch?: string }> = ({ batch }) =>
  batch ? (
    <span
      className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-px rounded border border-border bg-surface-2 text-fg-muted shrink-0"
      title={`Source batch: ${batch}`}
    >
      {batch}
    </span>
  ) : null;

interface CaseRowProps {
  study: StudyData;
  /** The row is a thin selectable entry: clicking sets the active study
   * (`onSelect`) so its gallery loads in the dominant right-pane stage, and
   * `isActive` drives the highlighted left-border. */
  isActive?: boolean;
  onSelect?: (studyId: string, anchorTop?: number) => void;
  /** When the row is clicked, the viewport-Y it was clicked at — so the
   * collapsed preview list can re-anchor the selection to ~that height instead
   * of snapping it to a list edge. */
  anchorTopRef?: React.MutableRefObject<number | null>;
  /** Full-width "worklist" layout (no preview pane open). Roomier, so the row
   * shows the full-word severity badge and packs all findings onto the second
   * line; when false the row is in the narrow "preview" list (triangle + a few
   * findings). Row height/structure is identical either way. */
  expanded?: boolean;
  /** Prototype acute/actionable severity (highest finding), computed once in
   * the study index so the badge matches the "Severity" sort. */
  severity?: Severity | null;
  /** Keyword guess or the device's clinical tier — picks the badge's wording. */
  severitySource?: SeveritySource;
  isSaved?: boolean;
  onToggleSave?: (studyId: string) => void;
  cohortId?: string;
  // Review-mode prop. When `disposition` is provided (even as `null` for
  // "not yet reviewed"), the row renders a read-only reviewed indicator;
  // otherwise the row renders the standard worklist layout. Editing the
  // verdict happens in the case-viewer's ReviewerPanel, not here.
  disposition?: DispositionOverlay | null;
  // Surfacer list mode: this reporter case is fully reviewed (every finding
  // has a non-pending verdict). Drives the dimmed "Reviewed" treatment shown
  // when "Show reviewed" is on, so revealed-reviewed rows read as done at a
  // glance instead of looking identical to outstanding ones.
  isReviewed?: boolean;
  // This case has been skipped by the reviewer (set aside — e.g. the report
  // references a separate report we aren't comparing against). Hidden by
  // default like reviewed; when revealed it gets the amber Skipped badge.
  isSkipped?: boolean;
  /** Stored skip reason — surfaced on the Skipped badge's hover title. */
  skipReason?: string;
  /** Stored skip note (only for the "other" reason) — appended to the title. */
  skipNote?: string;
  /** Right-click handler that opens the skip menu (surfacer rows only). */
  onContextMenu?: (e: React.MouseEvent, studyId: string) => void;
}

const VERDICT_CHIP: Record<DispositionVerdict, string> = {
  right: "bg-signal-tint text-signal-text-tint border-signal-stroke",
  wrong: "bg-surface-2 text-fg border-border-strong",
  borderline: "bg-surface text-fg-muted border-border-strong border-dashed",
};

/** Read-only reviewed indicator for the review worklist.
 *
 * Shows the verdict chip when the study has a disposition, or a muted
 * "Not reviewed" badge otherwise. Non-interactive: clicks bubble to the row,
 * so the reviewer opens the case-viewer, where the verdict is edited in the
 * ReviewerPanel — the single editor for the disposition (same DDB row). */
const DispositionIndicator: React.FC<{
  studyUid: string;
  disposition: DispositionOverlay | null;
}> = ({ studyUid, disposition }) => {
  if (!disposition) {
    return (
      <span
        className="shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded border border-border-strong text-fg-muted"
        data-testid={`disposition-${studyUid}`}
      >
        Not reviewed
      </span>
    );
  }
  return (
    <span
      className={`shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded border ${VERDICT_CHIP[disposition.verdict]}`}
      data-testid={`verdict-${studyUid}`}
      title={disposition.comment ? `“${disposition.comment}”` : undefined}
    >
      {disposition.verdict}
    </span>
  );
};

/** Amber "Skipped" badge — visually distinct from the green "✓ Reviewed"
 * badge so a revealed skipped case reads as set-aside, not done. The reason
 * (and any free-text note) is surfaced on hover. */
const SkippedBadge: React.FC<{ reason?: string; note?: string }> = ({ reason, note }) => {
  // `reason` is the arbitrary string stored on the skip row; widen the typed
  // label map to look it up with the fallback for unknown server vocabulary.
  const label = reason ? ((SKIP_REASON_LABEL as Record<string, string>)[reason] ?? reason) : "";
  const title = label ? `Skipped — ${label}${note ? `: ${note}` : ""}` : "Skipped";
  return (
    <span
      data-testid="skipped-badge"
      title={title}
      className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border border-warn/50 text-warn bg-warn-tint"
    >
      Skipped
    </span>
  );
};

/** Triage notification marker: the released device flagged this case. Encoded as
 * "attention" (border-strong + surface-2 lift) with an explicit text label. */
const TriageFlagBadge: React.FC = () => (
  <span
    data-testid="triage-flag-badge"
    className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border border-border-strong text-fg bg-surface-2"
  >
    Flagged
  </span>
);

/** Case-level severity marker — the HIGHEST finding severity in the case
 * (red = acute, orange = actionable; cases that mark as neither get nothing).
 * Two forms: a compact caution triangle for the space-constrained preview list
 * ("icon"), and the full uppercase word for the roomy full-width worklist
 * ("label"). Same data, different density. */
const SEVERITY_ICON_CLASS: Record<Severity, string> = {
  acute: "text-red-500",
  actionable: "text-orange-500",
};
const SEVERITY_LABEL_CLASS: Record<Severity, string> = {
  acute: "border-red-500/50 text-red-400 bg-red-500/15",
  actionable: "border-orange-500/50 text-orange-400 bg-orange-500/15",
};
const SEVERITY_LABEL: Record<SeveritySource, Record<Severity, string>> = {
  // Keyword-guessed rows: "acute" is the internal severity key, but the badge's
  // true signal is priority (a keyword heuristic, not a clinical acuity call) —
  // so label it "High priority" in the UI. The `acute` key itself is unchanged.
  keyword: { acute: "High priority", actionable: "Actionable" },
  // Reporter/surfacer rows carry the device's clinical_tier, so they use the
  // tier display names the case-viewer cards use (findingPriority.ts). Showing
  // "High priority" here read as the retired pre-2.0.0 `high_priority` tier.
  tier: { acute: priorityLabel("acute"), actionable: priorityLabel("prompt_workup") },
};
const SeverityBadge: React.FC<{
  severity: Severity;
  source?: SeveritySource;
  variant?: "icon" | "label";
}> = ({ severity, source = "keyword", variant = "icon" }) => {
  const label = SEVERITY_LABEL[source][severity];
  const title =
    source === "tier" ? `Highest clinical tier: ${label}` : `Highest finding severity: ${label}`;
  if (variant === "label") {
    return (
      <span
        data-testid={`severity-badge-${severity}`}
        title={title}
        className={`shrink-0 text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border ${SEVERITY_LABEL_CLASS[severity]}`}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      data-testid={`severity-badge-${severity}`}
      title={title}
      aria-label={`${label} severity`}
      className="shrink-0 inline-flex items-center"
    >
      <FaExclamationTriangle className={`w-3 h-3 ${SEVERITY_ICON_CLASS[severity]}`} />
    </span>
  );
};

/** Active-case preview stage. Mounted only while a row is expanded so the
 * bbox fetch is gated to the active study. `passing` is the per-study set of
 * condition keys detected at the recommended threshold (= the cohort row's
 * `detected_at_recommended`, threaded down from the Worklist study index);
 * the gallery shows its empty state when nothing passes — no fallback to
 * "all predictions". */
const ActiveCaseStage: React.FC<{
  studyId: string;
  deviceId: string;
  passing: Set<string>;
  meta?: string;
  onOpenReport: () => void;
}> = ({ studyId, deviceId, passing, meta, onOpenReport }) => {
  const { token, userId } = useAuth();
  const { data: bboxData } = useStudyBbox(token, userId, studyId);
  const predictions = bboxData?.predictions ?? {};
  return (
    <div className="flex flex-col h-full min-h-0">
      <PreviewGallery
        studyId={studyId}
        deviceId={deviceId}
        predictions={predictions}
        passing={passing}
        meta={meta}
        onOpenReport={onOpenReport}
      />
    </div>
  );
};

// Render a summary phrase (carrying `**finding**` markers) with the finding
// terms bold and the connecting words ("with", "and") plain, so the findings
// stand out when skimming. Plain segments render as bare strings (no key needed).
const renderSummaryText = (text: string): React.ReactNode =>
  parseBoldSegments(text).map((seg, i) =>
    seg.bold ? (
      <strong key={i} className="font-bold">
        {seg.text}
      </strong>
    ) : (
      seg.text
    ),
  );

const CaseRow: React.FC<CaseRowProps> = React.memo(
  ({
    study,
    isSaved,
    onToggleSave,
    cohortId,
    disposition,
    isActive,
    onSelect,
    anchorTopRef,
    expanded,
    severity,
    severitySource,
    isReviewed,
    isSkipped,
    skipReason,
    skipNote,
    onContextMenu,
  }) => {
    const { elementRef } = useInView({
      threshold: 0.1,
      rootMargin: "100px",
    });

    // When this row becomes the active (preview-selected) one, keep it visible
    // in the collapsed worklist and, when we know where it was clicked, re-anchor
    // it to ~that viewport height instead of snapping to a list edge.
    //
    // Opening the preview animates the worklist's collapse, which transiently
    // reflows the rows — so we can't measure on the next frame. Poll until the
    // row's position stabilizes (two equal frames), then anchor (clamped to stay
    // fully visible). Cap the wait and fall back to a plain scroll-into-view.
    useEffect(() => {
      if (!isActive) return;
      const el = elementRef.current;
      if (!el) return;
      const anchorTop = anchorTopRef?.current ?? null;
      const container = el.closest<HTMLElement>(".overflow-y-auto");
      if (anchorTop == null || !container) {
        requestAnimationFrame(() => el.scrollIntoView({ block: "nearest" }));
        return;
      }
      let raf = 0;
      let frames = 0;
      let lastTop = NaN;
      let stable = 0;
      const tick = () => {
        const top = el.getBoundingClientRect().top;
        stable = Math.abs(top - lastTop) < 0.5 ? stable + 1 : 0;
        lastTop = top;
        if (stable >= 2 || frames++ > 40) {
          if (stable >= 2) {
            const cc = container.getBoundingClientRect();
            const desiredTop = Math.max(cc.top, Math.min(anchorTop, cc.bottom - el.offsetHeight));
            container.scrollTop += el.getBoundingClientRect().top - desiredTop;
          } else {
            el.scrollIntoView({ block: "nearest" });
          }
          return;
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [isActive]);

    const handleContextMenu = useCallback(
      (e: React.MouseEvent) => onContextMenu?.(e, study.studyId),
      [onContextMenu, study.studyId],
    );

    const studyCollectionIds = useStudyCollectionIds(study.studyId, cohortId);

    const allFindings = useMemo(() => worklistFindings(study), [study]);
    const leadFindings = allFindings.slice(0, 2);
    // Full-width worklist packs every remaining finding onto the second line
    // (the row width truncates it); the narrow preview list shows just a few.
    const supportingFindings = expanded ? allFindings.slice(2) : allFindings.slice(2, 5);
    const totalFindings = allFindings.length;

    // A study is viewable before its model finishes, so an empty findings list
    // could mean "still running" rather than "completed, nothing found". Use the
    // inference status to disambiguate the empty-row copy below.
    const modelRunning = isModelRunning(study.inferenceStatus);
    // Terminal failure (e.g. OOM): the model produced no result. Surface the
    // reason instead of letting the empty findings list read as a clean negative.
    const modelFailed = isModelFailed(study.inferenceStatus);

    const isFullyLoaded = study.isLoaded;

    // Click row to select it — sets the active study so its gallery loads in
    // the dominant right-pane stage. The reporter/no-findings branches reuse
    // this same handler.
    const handleRowClick = useCallback(
      (e: React.MouseEvent) => {
        onSelect?.(study.studyId, e.currentTarget.getBoundingClientRect().top);
      },
      [study.studyId, onSelect],
    );

    const leadText = leadFindings.map((f) => formatFindingName(f.finding)).join(", ");
    const supportingText = supportingFindings.map((f) => formatFindingName(f.finding)).join(", ");
    // Busy cortex studies (>= 4 findings) on precomputed cohorts collapse to a
    // single summary phrase in place of the lead/supporting lines; the full list
    // moves to the title. Null = render the normal lead + supporting layout.
    const summaryPhrase = getFindingSummary(study, cohortId, totalFindings);
    const allFindingsText = allFindings.map((f) => formatFindingName(f.finding)).join(", ");

    // Loading state
    if (!isFullyLoaded) {
      return (
        <div
          ref={elementRef as React.RefObject<HTMLDivElement>}
          className="px-4 py-3 border-b border-border animate-pulse"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-4 bg-surface rounded" />
            <div className="flex-1">
              <div className="h-4 bg-surface rounded w-1/3 mb-1" />
              <div className="h-3 bg-surface rounded w-1/2" />
            </div>
          </div>
        </div>
      );
    }

    // Reporter cohort: the curated surfacer-billable missed set (the cards the
    // SurfacerPanel shows; filter mirrors src/surfacer.py:filter_candidates).
    // `isReporterStudy` is the base discriminator; `reporterMissedCount` is the
    // missing-surfacer count layered on top — see utils/reporterStudy.ts.
    if (isReporterStudy(study)) {
      const missedCount = reporterMissedCount(study);
      const surfacerFindings = study.surfacerFindings ?? [];
      // Down-rank: predicted-noise findings (report_mentions / no_report_coverage)
      // drop out of the row summary, mirroring the SurfacerPanel sink.
      // likelyFindings.length === missedCount by construction (same predicate).
      const likelyFindings = surfacerFindings.filter(
        (f) => !isPredictedNoise(f.predicted_verdict ?? null),
      );
      const noiseCount = surfacerFindings.length - likelyFindings.length;
      const surfLead = likelyFindings.slice(0, 2);
      const surfSupporting = likelyFindings.slice(2, 5);
      const surfRemaining = Math.max(0, likelyFindings.length - 5);
      const surfLeadText = surfLead.map((f) => formatConditionDisplay(f.finding)).join(", ");
      const surfSupportingText = surfSupporting
        .map((f) => formatConditionDisplay(f.finding))
        .join(", ");
      // Chip shows OP advancement (+N steps stricter than recommended the AI
      // fired) over the cohort percentile — the same key the list sorts by, so
      // the safety-net-first order reads on every row.
      const opScore = study.urgencyScore ?? null;
      const opPct = study.urgencyPercentile ?? null;
      return (
        <div
          data-testid="case-row"
          ref={elementRef as React.RefObject<HTMLDivElement>}
          onClick={handleRowClick}
          onContextMenu={handleContextMenu}
          className={`group px-4 py-2.5 border-b border-border hover:bg-surface-2 cursor-pointer transition-colors flex items-center gap-3 ${
            isReviewed || isSkipped ? "opacity-55" : ""
          }`}
        >
          <span className="w-10 shrink-0 flex flex-col items-center leading-none tabular-nums">
            {opScore != null ? (
              <>
                <span className="text-[11px] font-semibold text-signal-text-tint">+{opScore}</span>
                {opPct != null && (
                  <span className="text-[9px] text-fg-muted mt-0.5">{Math.round(opPct * 100)}</span>
                )}
              </>
            ) : (
              <span className="text-[10px] text-fg-muted">—</span>
            )}
          </span>
          {missedCount === 0 ? (
            <div className="flex-1 min-w-0">
              <span className="text-fg-muted text-sm italic">
                {noiseCount > 0
                  ? `No misses · ${noiseCount} likely reported / not covered`
                  : "No missed findings flagged for surfacer"}
              </span>
              <StudyMetaLine study={study} />
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-fg font-medium truncate text-sm">
                  {surfLeadText || `${missedCount} potentially missed by report`}
                </span>
                {studyCollectionIds.length > 0 && (
                  <FaStar className="w-3 h-3 text-signal-text-tint shrink-0" />
                )}
                {surfRemaining > 0 && (
                  <span className="text-fg-muted text-xs shrink-0">+{surfRemaining} more</span>
                )}
              </div>
              {surfSupportingText && (
                <div className="text-fg-muted text-xs truncate mt-0.5">{surfSupportingText}</div>
              )}
              <StudyMetaLine study={study} />
            </div>
          )}
          <span className="flex items-center gap-2 shrink-0">
            {!study.neuroSummary && <CaseReportDownloadButton studyId={study.studyId} compact />}
            {isSkipped && <SkippedBadge reason={skipReason} note={skipNote} />}
            {isReviewed && !isSkipped && (
              <span
                data-testid="reviewed-badge"
                className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border border-signal-stroke text-signal-text-tint bg-signal-tint"
              >
                ✓ Reviewed
              </span>
            )}
            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border border-border-strong text-fg-muted bg-surface-2">
              Surfacer
            </span>
            <span className="text-[10px] text-fg-muted font-mono tabular-nums">
              {study.studyId.split("::")[0]?.slice(-8) ?? ""}
            </span>
          </span>
        </div>
      );
    }

    // Empty state (still-running or genuinely clear). Same top-line + primary
    // structure as a findings row so all rows read as one family: status eyebrow
    // on the left lined up with the id on the right, primary spanning the row.
    // Pending and clear intentionally carry no subtitle.
    if (totalFindings === 0) {
      return (
        <div
          data-testid="case-row"
          ref={elementRef as React.RefObject<HTMLDivElement>}
          onClick={handleRowClick}
          className={`group px-4 py-3 border-b border-border hover:bg-surface-2 cursor-pointer transition-colors border-l-2 ${
            isActive ? "border-l-signal bg-signal-tint" : "border-l-transparent"
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 min-w-0">
              {modelRunning ? (
                <span
                  className="relative h-[3px] w-5 overflow-hidden rounded-none bg-border-strong shrink-0"
                  aria-label="Model still running"
                  title="Model still running"
                >
                  {/* The app's one defined loading motion: the signal-blue
                      `fetch-sweep` bar (FetchProgressBar / loading placeholders). */}
                  <span className="fetch-sweep absolute inset-0" />
                </span>
              ) : modelFailed ? (
                <span
                  className="w-4 shrink-0 text-center text-[11px] font-bold text-destructive"
                  aria-label="Prediction errored"
                  title={study.inferenceMessage || "Prediction errored"}
                >
                  !
                </span>
              ) : (
                <span className="w-4 shrink-0 text-center text-[11px] font-semibold text-fg-muted">
                  —
                </span>
              )}
              <span
                className={`text-[9px] font-bold uppercase tracking-widest ${
                  modelFailed
                    ? "text-destructive"
                    : modelRunning
                      ? "text-signal-text-tint"
                      : "text-fg-muted"
                }`}
              >
                {study.neuroSummary
                  ? "Neuro"
                  : study.triageDevice
                    ? "Triage"
                    : modelFailed
                      ? "Errored"
                      : modelRunning
                        ? "Analyzing"
                        : "Clear"}
              </span>
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {!isDecisionDevice(study) && !modelRunning && !modelFailed && (
                <CaseReportDownloadButton studyId={study.studyId} compact />
              )}
              {studyCollectionIds.length > 0 && (
                <FaStar className="w-3 h-3 text-signal-text-tint" />
              )}
              <BatchTag batch={study.batch} />
              <span className="text-[10px] text-fg-muted font-mono tabular-nums">
                {study.studyId.split("::")[0]?.slice(-8) || study.studyId.slice(-8)}
              </span>
            </span>
          </div>
          {/* Failed is bright (destructive) because it's an actionable error,
              not a placeholder; pending + clear stay dim. The full reason
              (e.g. the OOM message) is on the eyebrow's title. */}
          <div
            className={`mt-1 truncate text-sm font-medium ${
              modelFailed ? "text-destructive" : "text-fg-muted"
            }`}
          >
            {study.neuroSummary
              ? study.neuroSummary.label
              : study.triageDevice
                ? (study.triageSummary?.label ?? "Results loading")
                : modelFailed
                  ? study.inferenceMessage?.match(/out\s*of\s*memory/i)
                    ? "Prediction errored: out of memory (study too large)"
                    : "Prediction errored"
                  : modelRunning
                    ? "Findings pending"
                    : "No findings detected"}
          </div>
        </div>
      );
    }

    return (
      <div
        data-testid="case-row"
        ref={elementRef as React.RefObject<HTMLDivElement>}
        onClick={handleRowClick}
        className={`group px-4 py-3 border-b border-border hover:bg-surface-2 cursor-pointer transition-colors border-l-2 ${
          isActive ? "border-l-signal bg-signal-tint" : "border-l-transparent"
        }`}
      >
        {/* Top line: complexity bars + status eyebrow (left), id + controls (right).
            The status lines up with the id so the finding names span the full row. */}
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 min-w-0">
            {/* Wifi-style complexity bars (squared) — a finding-count proxy that
                discourages cherry-picking; see complexity.ts. */}
            <ComplexityBars level={complexityLevel(totalFindings)} />
            <span className="text-[9px] font-bold uppercase tracking-wide whitespace-nowrap shrink-0 text-signal-text-tint">
              {(study.neuroSummary ?? study.triageSummary)?.label ??
                `${totalFindings} ${totalFindings === 1 ? "Finding" : "Findings"}`}
            </span>
            {severity && (
              <SeverityBadge
                severity={severity}
                source={severitySource}
                variant={expanded ? "label" : "icon"}
              />
            )}
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            {!isDecisionDevice(study) && (
              <CaseReportDownloadButton studyId={study.studyId} compact />
            )}
            {/* Bookmark — visible on hover, stays when saved. */}
            {onToggleSave && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleSave(study.studyId);
                }}
                className={`p-1.5 rounded transition-all shrink-0 ${
                  isSaved
                    ? "text-signal-text-tint hover:text-signal-text-tint hover:bg-signal-tint"
                    : "text-fg-muted opacity-0 group-hover:opacity-100 hover:text-signal-text-tint hover:bg-surface-2"
                }`}
                title={isSaved ? "Remove from saved" : "Save for later"}
              >
                {isSaved ? (
                  <FaBookmark className="w-3.5 h-3.5" />
                ) : (
                  <FaRegBookmark className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            {/* Collection indicator — placed after the bookmark (bookmark, then star). */}
            {studyCollectionIds.length > 0 && <FaStar className="w-3 h-3 text-signal-text-tint" />}
            {/* Reviewed indicator (review mode) — to the right of bookmark + star. */}
            {disposition !== undefined && (
              <DispositionIndicator
                studyUid={study.studyId.split("::")[0] ?? study.studyId}
                disposition={disposition}
              />
            )}
            <BatchTag batch={study.batch} />
            {/* Case number — flush right, aligned with where the row text ends. */}
            <span className="text-[10px] text-fg-muted font-mono tabular-nums">
              {study.studyId.split("::")[0]?.slice(-8) || study.studyId.slice(-8)}
            </span>
          </span>
        </div>

        {/* Primary (title): the summary phrase when present, else lead findings
            capped to ONE row. The "N Findings" eyebrow carries the full count; a
            trailing ", …" implies more when the title shows fewer than exist. The
            full finding list is on the title attr whenever a summary replaces it. */}
        <div
          className={`mt-1 truncate text-sm text-white ${summaryPhrase ? "" : "font-medium"}`}
          title={summaryPhrase ? allFindingsText : undefined}
        >
          {summaryPhrase ? (
            renderSummaryText(summaryPhrase)
          ) : (
            <>
              {leadText || "—"}
              {totalFindings > leadFindings.length && <span className="text-fg-muted">, …</span>}
            </>
          )}
        </div>

        {/* Subtitle: supporting findings. Single truncated line in BOTH layouts
            so a row is the same height in the worklist and the preview list
            (the worklist just packs more findings before the row width cuts off).
            Suppressed when a summary phrase already stands in for the whole list. */}
        {!summaryPhrase && supportingText && (
          <div className="text-fg-muted text-xs mt-0.5 truncate">{supportingText}</div>
        )}
        <StudyMetaLine study={study} />
      </div>
    );
  },
);

// =============================================================================
// EXPERIMENTAL TABLE LAYOUT — the full-width worklist rendered as columns
// (AI Results · Severity · Status · Exam · Case). Used only in the expanded
// worklist; the collapsed preview keeps the thin CaseRow list above. The header
// and every row share WORKLIST_GRID so the columns line up.
// =============================================================================
const WORKLIST_GRID = "grid-cols-[5.5rem_7rem_minmax(0,2.4fr)_8rem_minmax(0,1.1fr)_7rem]";

const WorklistTableHeader: React.FC = () => (
  <div
    className={`grid ${WORKLIST_GRID} items-center gap-3 px-4 py-2 sticky top-0 z-10 bg-bg-2 border-b border-border-strong text-[9px] font-mono uppercase tracking-[0.16em] text-fg-dim`}
  >
    <span>AI Results</span>
    <span>Category</span>
    <span>Findings</span>
    <span>Status</span>
    <span>Exam</span>
    <span className="text-right">Case</span>
  </div>
);

// Stable IntersectionObserver options for the lazy exam-label fetch — a module
// const so the observer isn't torn down/recreated each render (useInView's
// effect depends on the options object identity).
const TABLE_ROW_INVIEW = { threshold: 0.1, rootMargin: "200px" };

interface CaseTableRowProps {
  study: StudyData;
  isActive?: boolean;
  onSelect?: (studyId: string, anchorTop?: number) => void;
  /** Prototype acute/actionable severity (highest finding), from the study
   * index — shared with the "Severity" sort so badge and sort never disagree. */
  severity?: Severity | null;
  severitySource?: SeveritySource;
  isSaved?: boolean;
  onToggleSave?: (studyId: string) => void;
  cohortId?: string;
  disposition?: DispositionOverlay | null;
  isReviewed?: boolean;
  isSkipped?: boolean;
  skipReason?: string;
  skipNote?: string;
  onContextMenu?: (e: React.MouseEvent, studyId: string) => void;
}

const CaseTableRow: React.FC<CaseTableRowProps> = React.memo(
  ({
    study,
    isActive,
    onSelect,
    severity,
    severitySource,
    isSaved,
    onToggleSave,
    cohortId,
    disposition,
    isReviewed,
    isSkipped,
    skipReason,
    skipNote,
    onContextMenu,
  }) => {
    // Lazy exam-label fetch: only once the row scrolls into view (hasLoaded is
    // sticky), so a page-load doesn't fire 25 metadata requests at once. Cached
    // per study by react-query, so scroll-back is free. `retry` skips the
    // global retry:1 for the 423 (not-canonicalized) case — the exam label is
    // best-effort, so we don't double-hit that heavy endpoint to land on "—".
    const { token, userId } = useAuth();
    const { elementRef, hasLoaded } = useInView(TABLE_ROW_INVIEW);
    const { data: studyMeta } = useStudyMetadata(token, userId, study.studyId, {
      enabled: hasLoaded,
      retry: (_count, err) => !(err instanceof StudyNotCanonicalizedError),
    });
    const examText = examLabelFromMetadata(studyMeta);
    const studyCollectionIds = useStudyCollectionIds(study.studyId, cohortId);

    const isReporter = isReporterStudy(study);
    const cortexFindings = useMemo(() => worklistFindings(study), [study]);
    const surfacerFindings = study.surfacerFindings ?? [];
    const modelRunning = isModelRunning(study.inferenceStatus);
    const modelFailed = isModelFailed(study.inferenceStatus);

    // Row kind drives the columns: reporter (surfacer misses), cortex with
    // findings, or cortex empty (pending / clear / errored).
    const decision = study.neuroSummary ?? study.triageSummary;
    const count = decision
      ? decision.count
      : isReporter
        ? surfacerFindings.length
        : cortexFindings.length;

    // For busy cortex studies (>= 4 findings) on cohorts we've precomputed,
    // show a short LLM summary phrase instead of the full comma-joined list
    // (which the row width just truncates mid-list). The full list moves to the
    // cell `title` so hover still reveals everything. Returns null otherwise.
    const cortexListText = cortexFindings.map((f) => formatFindingName(f.finding)).join(", ");
    const cortexSummary =
      !isReporter && cortexFindings.length > 0
        ? getFindingSummary(study, cohortId, cortexFindings.length)
        : null;

    const categoryText = decision
      ? cortexListText || decision.label
      : isReporter
        ? surfacerFindings.length > 0
          ? surfacerFindings.map((f) => formatConditionDisplay(f.finding)).join(", ")
          : "No missed findings flagged"
        : cortexFindings.length > 0
          ? (cortexSummary ?? cortexListText)
          : modelFailed
            ? study.inferenceMessage?.match(/out\s*of\s*memory/i)
              ? "Prediction errored: out of memory (study too large)"
              : "Prediction errored"
            : modelRunning
              ? "Findings pending"
              : "No findings detected";

    const studyUid = study.studyId.split("::")[0] ?? study.studyId;
    const caseId = studyUid.slice(-8);
    const showRunningSweep = !isReporter && count === 0 && modelRunning;

    return (
      <div
        data-testid="case-row"
        ref={elementRef as React.RefObject<HTMLDivElement>}
        onClick={(e) => onSelect?.(study.studyId, e.currentTarget.getBoundingClientRect().top)}
        onContextMenu={onContextMenu ? (e) => onContextMenu(e, study.studyId) : undefined}
        className={`group grid ${WORKLIST_GRID} items-center gap-3 px-4 py-2.5 border-b border-border hover:bg-surface-2 cursor-pointer transition-colors border-l-2 ${
          isActive ? "border-l-signal bg-signal-tint" : "border-l-transparent"
        }`}
      >
        {/* AI Results — finding COUNT (complexity-bar glyph), or the loading
            sweep while the model is still running. */}
        <div className="flex items-center gap-2 min-w-0">
          {showRunningSweep ? (
            <span
              className="relative h-[3px] w-5 overflow-hidden rounded-none bg-border-strong shrink-0"
              aria-label="Model still running"
              title="Model still running"
            >
              <span className="fetch-sweep absolute inset-0" />
            </span>
          ) : (
            <>
              {count !== null && <ComplexityBars level={complexityLevel(count)} />}
              <span className="text-xs font-semibold tabular-nums text-signal-text-tint">
                {count ?? "—"}
              </span>
            </>
          )}
        </div>
        {/* Severity */}
        <div className="min-w-0">
          {severity ? (
            <SeverityBadge severity={severity} source={severitySource} variant="label" />
          ) : (
            <span className="text-fg-faint text-xs">—</span>
          )}
        </div>
        {/* Category — finding names (or the model status when none). When a
            summary phrase replaces the list, finding terms render bold against a
            normal-weight base (so they pop), and the full list lives in the title. */}
        <div className="min-w-0">
          <span
            className={`block truncate text-sm ${
              (count ?? 0) > 0
                ? cortexSummary
                  ? "text-white"
                  : "font-medium text-white"
                : "italic text-fg-muted"
            }`}
            title={cortexSummary || decision ? cortexListText : undefined}
          >
            {cortexSummary ? renderSummaryText(cortexSummary) : categoryText}
          </span>
        </div>
        {/* Status — review disposition, skip/reviewed badge, or model state. */}
        <div className="flex items-center gap-1.5 min-w-0">
          {disposition !== undefined ? (
            <DispositionIndicator studyUid={studyUid} disposition={disposition} />
          ) : isSkipped ? (
            <SkippedBadge reason={skipReason} note={skipNote} />
          ) : isReviewed ? (
            <span
              data-testid="reviewed-badge"
              className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border border-signal-stroke text-signal-text-tint bg-signal-tint"
            >
              ✓ Reviewed
            </span>
          ) : isReporter ? (
            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-px rounded-xs border border-border-strong text-fg-muted bg-surface-2">
              Surfacer
            </span>
          ) : study.triageSummary?.flagged ? (
            <TriageFlagBadge />
          ) : decision ? (
            <span className="text-[10px] text-fg-muted">{decision.label}</span>
          ) : count === 0 ? (
            <span
              className={`text-[10px] font-bold uppercase tracking-wide ${
                modelFailed
                  ? "text-destructive"
                  : modelRunning
                    ? "text-signal-text-tint"
                    : "text-fg-muted"
              }`}
            >
              {modelFailed ? "Errored" : modelRunning ? "Analyzing" : "Clear"}
            </span>
          ) : null}
        </div>
        {/* Exam description (approximate; lazy-fetched from study metadata). */}
        <div className="min-w-0">
          <span className="block truncate text-xs text-fg-muted">{examText || "—"}</span>
        </div>
        {/* Case — bookmark (hover) + collection star + batch + id. */}
        <div className="flex items-center gap-2 justify-end min-w-0">
          {!isDecisionDevice(study) && !modelRunning && !modelFailed && (
            <CaseReportDownloadButton studyId={study.studyId} compact />
          )}
          {onToggleSave && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onToggleSave(study.studyId);
              }}
              className={`p-1 rounded transition-all shrink-0 ${
                isSaved
                  ? "text-signal-text-tint"
                  : "text-fg-muted opacity-0 group-hover:opacity-100 hover:text-signal-text-tint"
              }`}
              title={isSaved ? "Remove from saved" : "Save for later"}
            >
              {isSaved ? <FaBookmark className="w-3 h-3" /> : <FaRegBookmark className="w-3 h-3" />}
            </button>
          )}
          {studyCollectionIds.length > 0 && <FaStar className="w-3 h-3 text-signal-text-tint" />}
          <BatchTag batch={study.batch} />
          <span className="text-[11px] text-fg-muted font-mono tabular-nums truncate">
            {caseId}
          </span>
        </div>
      </div>
    );
  },
);

const Worklist: React.FC<WorklistProps> = ({ viewMode = "list" }) => {
  const location = useLocation();
  // Extract studyId from URL path for wildcard routes (/case-viewer/*, /reporter/*)
  // This handles study IDs with slashes like "bowel_obstruction/GRDNU1W0HCMZUSAB_GRD"
  const studyId = useMemo(() => {
    const prefix =
      viewMode === "case" ? "/case-viewer/" : viewMode === "reporter" ? "/reporter/" : null;
    if (prefix && location.pathname.startsWith(prefix)) {
      return location.pathname.slice(prefix.length) || undefined;
    }
    return undefined;
  }, [location.pathname, viewMode]);
  const [studies, setStudies] = useState<StudyData[]>([]);
  const navigate = useNavigate();
  const { token, userId } = useAuth();

  // ==============================================================================
  // PAGINATION - Only load visible page of studies to avoid massive batch requests
  // ==============================================================================
  const PAGE_SIZE = 25;
  const [currentPage, setCurrentPage] = useState(0);

  // ==============================================================================
  // COHORT FILTERING - Filter by regulatory run/cohort
  // ==============================================================================
  const [searchParams, setSearchParams] = useSearchParams();
  // In review mode the cohort is a path param (/review/cohort/:cohortId);
  // everywhere else it's a query string (/worklist?cohort=...).
  const routeParams = useParams<{ cohortId?: string }>();
  const cohortFromRoute =
    viewMode === "review" ? routeParams.cohortId || null : searchParams.get("cohort");
  // Eagerly initialize Cornerstone while the user browses the worklist.
  // This front-loads the ~2-5s dynamic import + WebGL + worker setup so that
  // segmentation tools are available immediately when a case is opened.
  useEffect(() => {
    initializeCornerstone().catch((err) =>
      console.warn("[Worklist] Cornerstone init failed:", err),
    );
  }, []);

  // ==============================================================================
  // READER COHORT - Single pre-aggregated query returns all studies + detection
  // flags in one shot from the a2z-reader-derived table.
  // ==============================================================================
  const { data: cohorts } = useCohorts(token);

  // Review mode reads from /api/review/cohort/<id> (reader cohort overlaid with
  // the caller's dispositions). Response is a superset of the reader cohort —
  // same shape plus a `disposition` field per study + meta.caller_reviewed.
  const isReviewMode = viewMode === "review";
  // Reviewers don't get the internal cohort list (it leaks device/cohort IDs);
  // their sidebar shows only the cohorts granted on the JWT (`allowed_cohorts`).
  const allowedCohorts = useMemo(
    () => (token ? (decodeToken(token)?.allowed_cohorts ?? []) : []),
    [token],
  );
  // Stable role signal used to gate internal cohort names.
  const userType = useAtomValue(userTypeAtom);
  // The URL is the selection source of truth. Async config resolves a collection
  // to its cohorts; there is no atom/local-state mirror that can erase a link.
  const selectedCollectionId = isReviewMode ? null : searchParams.get("collection");
  const {
    collections,
    isPending: collectionsLoading,
    isError: collectionsError,
    refetch: retryCollections,
  } = useCollections();
  const activeCollection = selectedCollectionId ? collections.get(selectedCollectionId) : undefined;
  const hasExplicitCaseCohort =
    (viewMode === "case" || viewMode === "reporter") && !!cohortFromRoute;
  const selectedCohortId =
    selectedCollectionId && selectedCollectionId !== "__saved__" && !hasExplicitCaseCohort
      ? (activeCollection?.cohortId ?? null)
      : cohortFromRoute;
  const isMultiCohort = !isReviewMode && (activeCollection?.cohortIds.length ?? 0) > 1;

  // One navigation updates both identifiers. Review mode owns its path-based
  // cohort selection and never uses this list selector.
  const selectList = useCallback(
    (collectionId: string | null, cohortId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (collectionId) next.set("collection", collectionId);
          else next.delete("collection");
          if (cohortId) next.set("cohort", cohortId);
          else next.delete("cohort");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  useEffect(() => setCurrentPage(0), [selectedCollectionId, selectedCohortId]);

  const readerQuery = useReaderCohort(token, isReviewMode ? null : selectedCohortId);
  const reviewQuery = useReviewCohort(token, isReviewMode ? selectedCohortId : null, userId);
  // useQueries must be called unconditionally — pass [] when not merging.
  const multiCohortQuery = useReaderCohorts(
    token,
    isMultiCohort ? activeCollection!.cohortIds : [],
  );
  const {
    data: readerData,
    isLoading,
    isError: isCohortError,
    refetch: refetchCohort,
  } = isReviewMode ? reviewQuery : isMultiCohort ? multiCohortQuery : readerQuery;

  const neuroSummaries = useNeuroWorklist(token, userId, readerData?.studies);
  const triageSummaries = useTriageWorklist(token, userId, readerData?.studies);

  const readerStudies = useMemo<StudyData[]>(() => {
    if (!readerData?.studies) return [];
    return readerData.studies.map(
      (item) =>
        ({
          studyId: readerCaseId(item),
          series: [],
          isLoaded: true,
          isLoading: false,
          predictions: {},
          activeModelType: "experimental" as const,
          report: "",
          metadata: {},
          device_id: item.model,
          inferenceStatus: item.status,
          inferenceMessage: item.inference_message,
          batch: item.batch,
          analyzedSeriesId: item.analyzed_series_id,
          findingsByOp: isDecisionItem(item)
            ? undefined
            : buildFindingsByOp(item, getDetectedThreshold(item.model)),
          neuroSummary: neuroSummaries[readerCaseId(item)],
          triageDevice: item.triage_device,
          triageSummary: item.triage_device ? triageSummaries[readerCaseId(item)] : undefined,
          comparisonCounts: item.comparison_counts,
          surfacerFindings: item.surfacer_findings,
          urgencyScore: item.urgency_score ?? null,
          urgencyPercentile: item.urgency_percentile ?? null,
        }) satisfies StudyData,
    );
  }, [readerData, neuroSummaries, triageSummaries]);

  // Disposition lookup keyed by bare study_uid (not the studyId composite).
  // Only populated in review mode; empty map elsewhere.
  const dispositionByStudyUid = useMemo(() => {
    const m = new Map<string, DispositionOverlay | null>();
    if (!isReviewMode || !readerData?.studies) return m;
    // In review mode the query is ReviewerCohortResponse, whose `studies` are
    // ReviewerStudyItem (ReaderStudyItem + disposition). The shared variable
    // collapses to ReaderStudyItem in the union, so narrow here.
    const reviewerStudies = readerData.studies as unknown as readonly ReviewerStudyItem[];
    for (const item of reviewerStudies) {
      m.set(item.study_uid, item.disposition ?? null);
    }
    return m;
  }, [isReviewMode, readerData]);

  const effectiveStudyIds = useMemo(() => readerStudies.map((s) => s.studyId), [readerStudies]);

  // ==============================================================================
  // FILTER STATE - Device type and finding filters (Jotai atoms)
  // ==============================================================================
  const [selectedDeviceId, setSelectedDeviceId] = useAtom(selectedDeviceIdAtom);
  const [selectedFindings, setSelectedFindings] = useAtom(selectedFindingsAtom);
  const [findingFilterMode, setFindingFilterMode] = useAtom(findingFilterModeAtom);
  // selectedCollectionId + collections are read above (they gate the reader path).
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [sortMode, setSortMode] = useAtom(sortModeAtom);
  // Only the prioritization sorts animate their reorder (see ANIMATED_SORTS).
  const animateReorder = ANIMATED_SORTS.has(sortMode);
  // External demo users only navigate via curated Collections — the raw
  // Cohorts section leaks internal device + cohort IDs (e.g. "cortex-…",
  // "regulatory-drafter-…", "a2z-abdomen-pelvis-ct-suite-…"). Mirrors
  // backend INTERNAL_USER_TYPES = {admin, client, internal}.
  const isAdmin = useAtomValue(isAdminAtom);
  // `userType` is declared above with the collection selection.
  const isInternalUser = isAdmin || isInternalRole(userType);
  const queryClient = useQueryClient();

  // ==============================================================================
  // SURFACER REVIEW OVERLAY - The caller's verdicts across all studies, keyed by
  // study_uid. Joined into the study index below so a surfacer case drops off the
  // worklist once EVERY one of its findings has a non-pending verdict (mirrors the
  // saved-cases per-user overlay; one DynamoDB Query backs the whole worklist).
  // Gated to non-review mode (review uses its own disposition overlay) + an
  // authenticated session.
  // ==============================================================================
  const { data: allSurfacerDecisions } = useAllSurfacerDecisions(!isReviewMode && !!token);
  const decisionsByStudy = allSurfacerDecisions ?? EMPTY_DECISIONS;

  // ==============================================================================
  // SKIPPED OVERLAY - The caller's skipped cases, keyed by study_uid. Joined into
  // the study index below so a skipped case drops off the worklist (revealed by
  // the same "Show reviewed" toggle, with a distinct amber badge). One DynamoDB
  // Query backs the whole worklist; gated to non-review mode + an authed session.
  // ==============================================================================
  const { data: allSkipped } = useAllSkippedStudies(!isReviewMode && !!token);
  const skippedByStudy = allSkipped ?? EMPTY_SKIPPED;
  const { setSkipped } = useSkipCase();

  // ==============================================================================
  // ONLY UNJUDGED - Verdicts pooled across a surfacer version family. With the
  // toggle on, a reporter case drops off once every finding has a verdict from
  // anyone in the family (any version; internal staff pool every reviewer),
  // not only from the caller. Per-user preference, default on for internal
  // staff (useOnlyUnjudged). One review-state request per listed cohort, only
  // while the toggle is on and the list has reporter rows.
  // ==============================================================================
  const { onlyUnjudged, setOnlyUnjudged } = useOnlyUnjudged();
  const listCohortIds = useMemo(
    () =>
      isMultiCohort ? activeCollection!.cohortIds : selectedCohortId ? [selectedCohortId] : [],
    [isMultiCohort, activeCollection, selectedCohortId],
  );
  const hasReporterRows = !!readerData?.studies?.some((s) => s.comparison_counts);
  const reviewStates = useSurfacerReviewStates(
    listCohortIds,
    onlyUnjudged && hasReporterRows && !isReviewMode && !!token,
  );

  // ==============================================================================
  // STUDY INDEX - Derived from reader cohort data for fast filter/sort lookup.
  // Shape: studyId -> { device_id, findings, count }
  // ==============================================================================
  interface LocalIndexEntry {
    device_id: string;
    findings: string[];
    findingsSet: Set<string>;
    count: number | null;
    // Reporter/surfacer cohorts. `isReporter` is the base discriminator;
    // `hasMissed` is the missing-surfacer lens (the curated billable AI-only set
    // the retro stage shows). The list visibility gate keys on `hasMissed`, NOT
    // `urgencyScore` — `urgencyScore` is a derived SORT key, and a missing sort
    // key must never hide a case (see indexFilteredStudyIds). Mirrors
    // utils/reporterStudy.ts on the wire shape.
    isReporter: boolean;
    hasMissed: boolean;
    // True when every surfacer finding on this reporter row has a non-pending
    // verdict from the caller — i.e. the case has been fully reviewed and should
    // fall off the worklist (unless "show completed" is on). Always false for
    // cortex/drafter rows. Joined from the bulk surfacer-decisions overlay.
    surfacerReviewed: boolean;
    // True when the caller has skipped this case (set aside). Skip is a
    // surfacer-cohort feature, so this is only ever set on reporter rows;
    // cortex/drafter rows are never skippable. Joined from the bulk skipped
    // overlay. Skipped cases fall off the list like reviewed ones (unless
    // show-completed).
    skipped: boolean;
    // "Only unjudged" is on and every surfacer finding on this reporter row has
    // a verdict from someone in the version family (pooled review state, plus
    // the caller's own verdicts). Such a case falls off like a reviewed one.
    // False while the toggle is off or the row's review state hasn't loaded.
    familyJudged: boolean;
    skipReason: string;
    skipNote: string;
    // The study's true cohort. In a merged multi-cohort collection each study
    // carries its source cohort (stamped by mergeReaderCohorts); single-cohort
    // views fall back to the cohort meta. Used so a skip write records the
    // study's real cohort_id, not the collection's representative one.
    cohortId: string;
    urgencyScore: number | null;
    urgencyPercentile: number | null;
    // Highest-severity classification across the row's findings (acute >
    // actionable > null). Drives the "Severity" sort; same rule the CaseRow
    // badge uses. See config/findingSeverity.ts (prototype, frontend-only).
    // Reporter rows use the device's clinical_tier instead (severitySource "tier").
    severity: Severity | null;
    severitySource: SeveritySource;
    // Source-delivery subcohort (cal46/emo32/...) when the cohort has a batch
    // axis; drives the worklist's subcohort filter chips. Undefined otherwise.
    batch?: string;
    // Released triage device row and whether it carries at least one flag. Drives
    // the "Flagged first" sort.
    triageDevice: boolean;
    flagged: boolean;
  }
  const studyIndex = useMemo<Record<string, LocalIndexEntry>>(() => {
    const map: Record<string, LocalIndexEntry> = {};
    const metaCohortId = readerData?.meta.cohort_id ?? "";
    for (const item of readerData?.studies ?? []) {
      const id = readerCaseId(item);
      // Merged views stamp each study with its source cohort; single-cohort
      // views carry it only in meta.
      const cohortId = item.cohort_id ?? metaCohortId;
      // Skip state is case-level, keyed by bare study_uid (not the studyId
      // composite). Only joined for surfacer (reporter) rows below — skip is a
      // surfacer-cohort feature, so cortex/drafter rows stay unskippable.
      const skipInfo = skippedByStudy[item.study_uid];
      // Reporter rows have comparison_counts, not detected_at_*. Surface the
      // ai_only count at recommended OP as "missed by report" — that's the
      // surfacer signal (cortex saw it, the radiologist's report didn't).
      // The lambda raises if recommended_threshold is missing on any entry,
      // so `recommended` is always populated for ingested reporter cohorts.
      if (item.comparison_counts) {
        const missed = item.comparison_counts.recommended?.ai_only ?? 0;
        const surfacerFindings = item.surfacer_findings ?? [];
        const studyDecisions = decisionsByStudy[cohortId]?.[item.study_uid];
        // Fully reviewed iff the case has findings AND each one carries a
        // non-pending verdict. Empty/absent decisions ⇒ not reviewed (stays).
        const surfacerReviewed =
          surfacerFindings.length > 0 &&
          surfacerFindings.every((f) => {
            const verdict = studyDecisions?.[f.finding];
            return verdict != null && verdict !== "pending";
          });
        const unjudged = onlyUnjudged
          ? unjudgedFindings(item, metaCohortId, reviewStates.byCohort, decisionsByStudy)
          : null;
        map[id] = {
          device_id: item.model,
          findings: [],
          findingsSet: new Set<string>(),
          count: missed,
          isReporter: true,
          hasMissed: surfacerFindings.length > 0,
          surfacerReviewed,
          skipped: !!skipInfo,
          familyJudged: surfacerFindings.length > 0 && unjudged !== null && unjudged.length === 0,
          skipReason: skipInfo?.reason ?? "",
          skipNote: skipInfo?.reason_note ?? "",
          cohortId,
          urgencyScore: item.urgency_score ?? null,
          urgencyPercentile: item.urgency_percentile ?? null,
          // The device's own clinical_tier on each surfaced finding — not the
          // keyword guess. A row whose tiers are unknown (legacy reporter rows
          // not yet re-ingested) gets no badge rather than a guessed one.
          severity: tierCaseSeverity(surfacerFindings.map((f) => f.category)),
          severitySource: "tier",
          batch: item.batch,
          triageDevice: false,
          flagged: false,
        };
        continue;
      }
      const neuro = neuroSummaries[id];
      const triage = item.triage_device ? triageSummaries[id] : undefined;
      const decision = neuro ?? triage;
      const threshold = isDecisionItem(item) ? null : getDetectedThreshold(item.model);
      const detected =
        threshold === null
          ? (decision?.findings ?? [])
          : threshold === "recommended"
            ? (item.detected_at_recommended ?? [])
            : ((item[READER_OP_KEYS[threshold]] as string[] | undefined) ?? []);
      map[id] = {
        device_id: item.model,
        findings: detected,
        findingsSet: new Set(detected),
        count: decision ? decision.count : detected.length,
        isReporter: false,
        hasMissed: false,
        surfacerReviewed: false,
        // Cortex/drafter rows are never skippable (skip is surfacer-only).
        skipped: false,
        familyJudged: false,
        skipReason: "",
        skipNote: "",
        cohortId,
        urgencyScore: null,
        urgencyPercentile: null,
        severity: decision || item.triage_device ? null : caseSeverity(detected),
        severitySource: "keyword",
        batch: item.batch,
        triageDevice: !!item.triage_device,
        flagged: !!triage?.flagged,
      };
    }
    return map;
  }, [
    readerData,
    decisionsByStudy,
    skippedByStudy,
    neuroSummaries,
    triageSummaries,
    onlyUnjudged,
    reviewStates,
  ]);

  // ==============================================================================
  // SAVED CASES - Personal bookmarks
  // ==============================================================================
  const { data: savedCaseIds } = useSavedCases(token);
  const savedCaseSet = useMemo(() => new Set(savedCaseIds || []), [savedCaseIds]);

  const handleToggleSave = useCallback(
    async (studyId: string) => {
      if (!token) {
        console.error("[SaveCase] No auth token available");
        return;
      }
      try {
        if (savedCaseSet.has(studyId)) {
          await deleteSavedCase(token, studyId);
        } else {
          await saveCase(token, studyId);
        }
        queryClient.invalidateQueries({ queryKey: ["savedCases"] });
      } catch (err) {
        console.error("[SaveCase] Failed:", err);
      }
    },
    [token, savedCaseSet, queryClient],
  );

  // Local search within findings sidebar list
  const [findingSearch, setFindingSearch] = useState("");

  // Subcohort (source-delivery batch) filter. null = all. Only meaningful for
  // cohorts whose rows carry a `batch` (e.g. the Unilabs validation set:
  // cal46/emo32/ae_abdomen/...); the chip row hides itself when there are none.
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  // Subcohort breakdown for the chip row: { batch: studyCount }, from the index.
  const batchCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const entry of Object.values(studyIndex)) {
      if (entry.batch) counts[entry.batch] = (counts[entry.batch] ?? 0) + 1;
    }
    return counts;
  }, [studyIndex]);
  const batchNames = useMemo(() => Object.keys(batchCounts).sort(), [batchCounts]);
  // Stable cohort size for the "all" chip: the breakdown total, NOT the
  // filtered list count (which would shrink to the selected batch).
  const cohortStudyCount = useMemo(() => Object.keys(studyIndex).length, [studyIndex]);
  // A selected batch that no longer exists (cohort switch) must not strand the
  // filter and hide every row. Clear it when it falls out of the breakdown.
  useEffect(() => {
    if (selectedBatch && !batchCounts[selectedBatch]) setSelectedBatch(null);
  }, [selectedBatch, batchCounts]);

  // ==============================================================================
  // PREVIEW PANEL STATE - Quick preview of findings without full viewer (Jotai atom)
  // ==============================================================================
  const [previewStudyId, setPreviewStudyId] = useAtom(previewStudyIdAtom);
  // Preview open = a case is selected. Drives the worklist <-> preview layout:
  // false → full-width worklist (roomy rows, no stage); true → list collapses
  // to a thin left column with the dominant preview stage on the right.
  const previewOpen = !!previewStudyId;

  // Workstation header list-switcher + findings-filter popover open state.
  const [listSwitcherOpen, setListSwitcherOpen] = useState(false);
  const [findingsPopoverOpen, setFindingsPopoverOpen] = useState(false);
  // Surfacer worklist: reviewed cases drop off by default; this reveals them.
  const [showCompleted, setShowCompleted] = useState(false);

  // Skip context-menu: anchor + the row the right-click landed on. We keep both
  // the composite studyId (to look up the study's true cohort in the index) and
  // the bare study_uid (the skip row's key, also how skippedByStudy is keyed).
  const [skipMenu, setSkipMenu] = useState<{
    studyId: string;
    studyUid: string;
    x: number;
    y: number;
  } | null>(null);
  const handleRowContextMenu = useCallback((e: React.MouseEvent, sId: string) => {
    e.preventDefault();
    // Stop the opening contextmenu from reaching document, where
    // useContextMenu's dismiss listener — attached synchronously via React 18's
    // discrete-event effect flush — would otherwise close the menu on the very
    // event that opened it. Mirrors the reporter chip/section menu handlers.
    e.stopPropagation();
    setSkipMenu({ studyId: sId, studyUid: sId.split("::")[0] ?? sId, x: e.clientX, y: e.clientY });
  }, []);

  // ==============================================================================
  // INDEX-BASED FILTER LOGIC - Filter/search/sort across ALL studies before pagination
  // Uses server-side study index instead of per-page client-side filtering
  // ==============================================================================

  // Derive available devices from the index (covers ALL studies, not just current page)
  const availableDevices = useMemo(() => {
    const deviceCounts = new Map<string, number>();
    for (const entry of Object.values(studyIndex)) {
      const did = entry.device_id;
      if (!did || !did.startsWith("a2z-")) continue;
      deviceCounts.set(did, (deviceCounts.get(did) || 0) + 1);
    }
    return Array.from(deviceCounts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([device_id, study_count]) => ({
        device_id,
        display_name: device_id.startsWith("a2z-")
          ? device_id.slice(4).replace(/-/g, " ")
          : device_id,
        study_count,
      }));
  }, [studyIndex]);

  // Aggregate unique findings from index, filtered by selected device
  const allUniqueFindingsWithCounts = useMemo(() => {
    const findingCounts = new Map<string, number>();
    const findingLabels = new Map<string, string>();
    for (const entry of Object.values(studyIndex)) {
      if (selectedDeviceId && entry.device_id !== selectedDeviceId) continue;
      for (const finding of entry.findings) {
        findingCounts.set(finding, (findingCounts.get(finding) || 0) + 1);
        findingLabels.set(
          finding,
          isNeuroModel(entry.device_id)
            ? neuroFindingLabel(finding)
            : entry.triageDevice
              ? finding
              : finding.split("/")[1],
        );
      }
    }

    return Array.from(findingCounts.entries())
      .map(([finding, count]) => ({ finding, count, label: findingLabels.get(finding)! }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.finding.localeCompare(b.finding);
      });
  }, [studyIndex, selectedDeviceId]);

  // Toggle a finding in the filter
  const toggleFindingFilter = useCallback(
    (finding: string) => {
      setSelectedFindings((prev) => {
        if (prev.includes(finding)) {
          return prev.filter((f) => f !== finding);
        } else {
          return [...prev, finding];
        }
      });
    },
    [setSelectedFindings],
  );

  // Clear all finding filters
  const clearFindingFilters = useCallback(() => {
    setSelectedFindings([]);
  }, [setSelectedFindings]);

  // Clear selected findings when device changes
  // (findings from one model may not exist in another)
  useEffect(() => {
    setSelectedFindings([]);
  }, [selectedDeviceId, setSelectedFindings]);

  // ==============================================================================
  // PREVIEW PANEL HANDLERS (see filteredStudies below — preview hooks moved
  // down so they can reference the paginated-and-filtered list)
  // ==============================================================================

  const handleOpenFullViewer = useCallback(
    (studyId: string) => {
      // Carry the reader row's true cohort into the case viewer. It cannot be
      // reconstructed from job_id when a review packet reuses an image job from
      // a different source cohort. Preserve collection context independently.
      const readerCohortId = studyIndex[studyId]?.cohortId || selectedCohortId;
      navigate(readerCaseViewerPath(studyId, readerCohortId, selectedCollectionId));
    },
    [navigate, selectedCohortId, selectedCollectionId, studyIndex],
  );

  // ==============================================================================
  // PAGINATION - Filter/search/sort ALL study IDs using index, THEN paginate
  // This fixes the bug where filters only applied to the current page
  // ==============================================================================
  // Step 1: Apply collection filter at ID level (skip for __saved__ - handled in step 2)
  const collectionFilteredIds = useMemo(() => {
    if (!activeCollection || selectedCollectionId === "__saved__") return effectiveStudyIds;
    return effectiveStudyIds.filter((id: string) => {
      const studyUid = id.split("::")[0];
      return activeCollection.studyUids.has(studyUid);
    });
  }, [effectiveStudyIds, activeCollection, selectedCollectionId]);

  // Step 2: Apply search + device + finding + saved filters using the index.
  // This is the BASE set — the surfacer-completion gate is layered on top
  // separately (below) so the count of reviewed-and-hidden cases is exact.
  const showSavedOnly = selectedCollectionId === "__saved__";
  const baseFilteredStudyIds = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();

    return collectionFilteredIds.filter((id: string) => {
      if (showSavedOnly && !savedCaseSet.has(id)) return false;

      const entry = studyIndex[id];
      if (!entry) return !showSavedOnly;

      if (selectedDeviceId && entry.device_id !== selectedDeviceId) return false;

      // Subcohort (batch) filter. One worklist, scoped to a delivery batch.
      if (selectedBatch && entry.batch !== selectedBatch) return false;

      // Surfacer-first: a reporter row only belongs on the worklist if it carries
      // a missed finding (the curated billable AI-only set the retro stage shows).
      // Gate on missed-finding PRESENCE, never on `urgencyScore`: the backend
      // leaves `urgency_score` null whenever a row's surfacer_findings lack a
      // numeric advancement (pre-backfill shape), and a missing SORT key must
      // never hide a CASE with genuine missed findings. Cortex/drafter rows
      // (isReporter false) are untouched, so a mixed collection keeps them too.
      if (entry.isReporter && !entry.hasMissed) return false;

      if (searchLower) {
        const studyUid = id.split("::")[0]?.toLowerCase() || id.toLowerCase();
        const findingMatch = entry.findings.some((f) => f.toLowerCase().includes(searchLower));
        const idMatch = studyUid.includes(searchLower);
        if (!findingMatch && !idMatch) return false;
      }

      if (selectedFindings.length > 0) {
        const hasMatch =
          findingFilterMode === "any"
            ? selectedFindings.some((f) => entry.findingsSet.has(f))
            : selectedFindings.every((f) => entry.findingsSet.has(f));
        if (!hasMatch) return false;
      }

      return true;
    });
  }, [
    collectionFilteredIds,
    studyIndex,
    searchQuery,
    selectedDeviceId,
    selectedBatch,
    selectedFindings,
    findingFilterMode,
    showSavedOnly,
    savedCaseSet,
  ]);

  // Step 2b: Surfacer "fall off once reviewed". A reporter case whose every
  // surfacer finding has a non-pending verdict drops off the list unless the
  // user opts into "show completed". The count is surfaced on the toggle so the
  // hide is observable, never silent.
  const surfacerReviewedCount = useMemo(
    () =>
      baseFilteredStudyIds.filter((id) => {
        const e = studyIndex[id];
        // A skipped case is set aside, not reviewed — count it under skipped only
        // (a case can be both, but it reads to the reviewer as skipped).
        return !!e?.isReporter && e.hasMissed && e.surfacerReviewed && !e.skipped;
      }).length,
    [baseFilteredStudyIds, studyIndex],
  );

  // Skipped cases hidden from the active list (any row type). Surfaced on the
  // toggle alongside the reviewed count so the hide is observable, never silent.
  const skippedCount = useMemo(
    () => baseFilteredStudyIds.filter((id) => studyIndex[id]?.skipped).length,
    [baseFilteredStudyIds, studyIndex],
  );

  // "Only unjudged": cases every finding of which someone in the family has
  // judged, that the caller hasn't fully reviewed or skipped themselves (those
  // are counted above), so each hidden case is counted once.
  const familyJudgedCount = useMemo(
    () =>
      baseFilteredStudyIds.filter((id) => {
        const e = studyIndex[id];
        return !!e?.familyJudged && !e.surfacerReviewed && !e.skipped;
      }).length,
    [baseFilteredStudyIds, studyIndex],
  );

  const hiddenCount = surfacerReviewedCount + familyJudgedCount + skippedCount;

  // One toggle reveals reviewed, judged and skipped cases (per the spec); the
  // label narrows to whichever is present so the count is honest.
  const {
    showLabel: showToggleLabel,
    hideLabel: hideToggleLabel,
    emptyLabel,
  } = hiddenToggleLabels(surfacerReviewedCount, familyJudgedCount, skippedCount);

  // "N unjudged left" beside the toggle: findings nobody has judged across the
  // listed (filtered, not skipped) reporter cases.
  const unjudgedLeft = useMemo(() => {
    if (!onlyUnjudged || !hasReporterRows || reviewStates.isLoading) return null;
    const listed = new Set(baseFilteredStudyIds);
    const rows = (readerData?.studies ?? []).filter((s) => listed.has(readerCaseId(s)));
    return unjudgedTally(
      rows,
      readerData?.meta.cohort_id ?? "",
      reviewStates.byCohort,
      decisionsByStudy,
      skippedByStudy,
    );
  }, [
    onlyUnjudged,
    hasReporterRows,
    reviewStates,
    baseFilteredStudyIds,
    readerData,
    decisionsByStudy,
    skippedByStudy,
  ]);

  const indexFilteredStudyIds = useMemo(() => {
    if (showCompleted) return baseFilteredStudyIds;
    return baseFilteredStudyIds.filter((id) => {
      const e = studyIndex[id];
      if (e?.skipped) return false;
      return !(e?.isReporter && (e.surfacerReviewed || e.familyJudged));
    });
  }, [baseFilteredStudyIds, showCompleted, studyIndex]);

  // Step 3: Sort filtered IDs using the index
  const sortedFilteredStudyIds = useMemo(() => {
    if (sortMode === "default") {
      // Reporter/surfacer cohorts carry a per-study urgency score → their
      // default order is safety-net-first (OP advancement desc, then cohort
      // percentile desc). Cohorts without urgency (cortex/drafter) keep the
      // backend's index order untouched.
      const hasUrgency = indexFilteredStudyIds.some((id) => studyIndex[id]?.urgencyScore != null);
      if (!hasUrgency) return indexFilteredStudyIds;
      return [...indexFilteredStudyIds].sort((a, b) =>
        compareUrgency(studyIndex[a], studyIndex[b]),
      );
    }

    return [...indexFilteredStudyIds].sort((a, b) => {
      const ea = studyIndex[a];
      const eb = studyIndex[b];
      if (!ea || !eb) return 0;

      // "Flagged first" only lifts flagged cases; a row still loading keeps its place.
      if (
        sortMode !== "alphabetical" &&
        sortMode !== "flagged" &&
        (ea.count === null || eb.count === null)
      ) {
        return Number(ea.count === null) - Number(eb.count === null);
      }
      const countDifference = (eb.count ?? 0) - (ea.count ?? 0);
      switch (sortMode) {
        case "findings_desc":
          return countDifference;
        case "findings_asc":
          return -countDifference;
        case "alphabetical":
          return a.localeCompare(b);
        case "severity":
          // Highest severity first (acute > actionable > unmarked); tie-break
          // by finding count desc so the busiest cases lead within a tier.
          return severityRank(eb.severity) - severityRank(ea.severity) || countDifference;
        case "flagged":
          // Triage: flagged cases move up; everything else keeps the default
          // order (sort is stable). Nothing moves down or drops off the list.
          return Number(eb.flagged) - Number(ea.flagged);
        default:
          return 0;
      }
    });
  }, [indexFilteredStudyIds, sortMode, studyIndex]);

  // Step 4: Resolve full filtered+sorted set to StudyData, then paginate.
  const preFilteredStudyIds = sortedFilteredStudyIds;
  const totalStudies = preFilteredStudyIds.length;
  const totalPages = Math.ceil(totalStudies / PAGE_SIZE);

  // Full filtered set (pre-pagination) — used for stage resolution and prev/next
  // traversal so the dominant stage is never blanked by a page change.
  const allFilteredStudies = useMemo(() => {
    const byId = new Map(studies.map((s) => [s.studyId, s]));
    return preFilteredStudyIds.map((id) => byId.get(id)).filter((s): s is StudyData => !!s);
  }, [studies, preFilteredStudyIds]);

  // Paginated window — used only for the list-column rendering.
  const filteredStudies = useMemo(() => {
    const start = currentPage * PAGE_SIZE;
    return allFilteredStudies.slice(start, start + PAGE_SIZE);
  }, [allFilteredStudies, currentPage]);

  // Prewarm the top case's model prediction (cached by the same query key the
  // case-viewer uses) so opening it lands on the model's auto-selected series
  // instead of the "Select primary series" picker. The dual-format worklist no
  // longer auto-selects a case on landing, which previously gave this warmup
  // implicitly; this restores it cheaply (one fetch) without selecting a case.
  useModelPrediction(
    token,
    userId,
    allFilteredStudies.find((study) => !isDecisionDevice(study))?.studyId ?? null,
  );
  // Prepare the same complete draft query used by the report editor.
  // Warm one completed upcoming case; never fan out over the whole worklist.
  const wordingPrefetchStudy = filteredStudies.find(
    (s) =>
      !isDecisionDevice(s) &&
      !isModelRunning(s.inferenceStatus) &&
      !isModelFailed(s.inferenceStatus),
  );
  const prefetchStudyId = wordingPrefetchStudy?.studyId ?? null;
  const prefetchReportState = useReportState(token, userId, prefetchStudyId);
  useReport(prefetchStudyId, {
    enabled:
      prefetchReportState.isSuccess &&
      !prefetchReportState.isFetching &&
      !!prefetchReportState.data &&
      !prefetchReportState.data.is_report_signed &&
      !prefetchReportState.data.structured_editor_state,
  });

  // Resolve the stage study from the FULL filtered set so a page change never
  // blanks the dominant stage. If previewStudyId was genuinely filtered out
  // (e.g. by a findings filter), this correctly returns null.
  const previewStudy = useMemo(() => {
    if (!previewStudyId) return null;
    return allFilteredStudies.find((s) => s.studyId === previewStudyId) || null;
  }, [previewStudyId, allFilteredStudies]);

  // Stage header meta (study desc · modality · device_id for internal users).
  // device_id leaks device/cohort identity — internal-only, same gate as the
  // cohort switcher. Computed once; both preview stages consume it.
  const previewStageMeta = previewStudy
    ? [
        previewStudy.study_description,
        previewStudy.modality,
        isInternalUser ? previewStudy.device_id : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  // Selecting a workstation row toggles the preview: clicking a new row opens it
  // in the dominant stage (collapsing the full-width worklist to the left);
  // clicking the already-active row again clears it, expanding back to the
  // full-width worklist. Drives the worklist <-> preview layout transition.
  // Viewport-Y the active row was clicked at, captured per click so the
  // collapsed worklist can re-anchor the selection to ~that height.
  const anchorTopRef = useRef<number | null>(null);
  // Mirror previewStudyId in a ref so handleSelectStudy can compute the toggle
  // without depending on it — keeps the callback stable (CaseRow's memo stays
  // intact) and lets us write anchorTopRef OUTSIDE the state updater (updaters
  // must stay pure; StrictMode double-invokes them).
  const previewStudyIdRef = useRef(previewStudyId);
  previewStudyIdRef.current = previewStudyId;
  const handleSelectStudy = useCallback(
    (id: string, anchorTop?: number) => {
      if (isDecisionDevice(studyIndex[id])) {
        handleOpenFullViewer(id);
        return;
      }
      const willOpen = previewStudyIdRef.current !== id;
      anchorTopRef.current = willOpen ? (anchorTop ?? null) : null;
      setPreviewStudyId(willOpen ? id : null);
    },
    [setPreviewStudyId, studyIndex, handleOpenFullViewer],
  );

  // ==============================================================================
  // One backend-driven list model serves both the chooser and the dropdown.
  // Raw cohorts remain internal-only; external users see curated collections.
  // ==============================================================================
  // Site triage worklists (every study + routing + device results) — internal only.
  const { data: triageWorklists } = useTriageWorklists(token, isInternalUser && !isReviewMode);
  const {
    data: devicePanels,
    isPending: panelsLoading,
    isError: panelsError,
    refetch: retryPanels,
  } = useDevicePanels(token, viewMode === "list");
  const listEntries = useMemo<ListEntry[]>(() => {
    if (isReviewMode) {
      // Reviewer cohort switcher: the cohorts granted on the JWT, plus the full
      // active-cohort list for internal users (admins carry allowed_cohorts=null,
      // so they'd otherwise get an empty switcher). Each entry navigates to the
      // review route — the path param is review mode's source of truth.
      const reviewEntries: ListEntry[] = [];
      const seen = new Set<string>();
      const pushReviewCohort = (id: string, label: string, count: number | null) => {
        if (seen.has(id)) return;
        seen.add(id);
        reviewEntries.push({
          key: `review:${id}`,
          kind: "cohort",
          label,
          count: selectedCohortId === id ? totalStudies : count,
          isActive: selectedCohortId === id,
          onSelect: () => navigate(`/review/cohort/${encodeURIComponent(id)}`),
        });
      };
      for (const c of allowedCohorts) pushReviewCohort(c, c, null);
      if (isInternalUser && cohorts) {
        for (const c of filterActiveCohorts(cohorts)) {
          const prefixMatch = c.run_id.match(/^([a-z]{3})-(.+)$/);
          const rest = prefixMatch ? prefixMatch[2] : c.run_id;
          const label = prefixMatch && c.display_name ? `${c.display_name} · ${rest}` : c.run_id;
          pushReviewCohort(c.run_id, label, c.total);
        }
      }
      return reviewEntries;
    }
    const entries: ListEntry[] = [];
    // Saved cases filter the active cohort; offer it only with that context.
    if (selectedCohortId)
      entries.push({
        key: "collection:__saved__",
        kind: "saved",
        label: "My Saved",
        count: savedCaseIds?.length ?? 0,
        isActive: selectedCollectionId === "__saved__",
        onSelect: () => selectList("__saved__", selectedCohortId),
      });
    for (const worklist of triageWorklists ?? []) {
      entries.push({
        key: `triage:${worklist.id}`,
        kind: "collection",
        label: worklist.name,
        count: worklist.count,
        isActive: false,
        onSelect: () => navigate(`/triage/${encodeURIComponent(worklist.id)}`),
      });
    }
    for (const panel of devicePanels ?? []) {
      entries.push({
        key: `panel:${panel.id}`,
        kind: "collection",
        label: panel.name,
        count: panel.study_count,
        isActive: false,
        onSelect: () => navigate(panelUrl(panel.id), { replace: true }),
      });
    }
    // Curated collections
    for (const collection of Array.from(collections.values())) {
      entries.push({
        key: `collection:${collection.id}`,
        kind: "collection",
        label: collection.name,
        count: collection.studyUids.size,
        isActive: selectedCollectionId === collection.id,
        onSelect: () => selectList(collection.id, null),
      });
    }
    // Internal cohorts — gated; external demos navigate via collections only.
    if (isInternalUser && cohorts) {
      for (const cohort of filterActiveCohorts(cohorts)) {
        const prefixMatch = cohort.run_id.match(/^([a-z]{3})-(.+)$/);
        const rest = prefixMatch ? prefixMatch[2] : cohort.run_id;
        const label =
          prefixMatch && cohort.display_name ? `${cohort.display_name} · ${rest}` : cohort.run_id;
        entries.push({
          key: `cohort:${cohort.run_id}`,
          kind: "cohort",
          label,
          count: cohort.total,
          isActive: selectedCohortId === cohort.run_id && !selectedCollectionId,
          onSelect: () => selectList(null, cohort.run_id),
        });
      }
    }
    return entries;
  }, [
    isReviewMode,
    isInternalUser,
    cohorts,
    collections,
    selectedCohortId,
    selectedCollectionId,
    savedCaseIds,
    totalStudies,
    selectList,
    allowedCohorts,
    navigate,
    triageWorklists,
    devicePanels,
  ]);

  // Active entry resolves the header label; fall back to the raw cohort id when
  // no entry matches (e.g. a deep-linked cohort not in the switcher list) so the
  // header never throws on a missing match.
  // A partner with one list has nothing to choose: open it in place of the picker,
  // once both list sources have loaded (a failed one leaves the picker to say so).
  const soleExternalList =
    viewMode === "list" &&
    !isInternalUser &&
    !selectedCohortId &&
    !selectedCollectionId &&
    !collectionsLoading &&
    !panelsLoading &&
    !collectionsError &&
    !panelsError &&
    listEntries.length === 1
      ? listEntries[0]
      : null;
  useEffect(() => {
    soleExternalList?.onSelect();
  }, [soleExternalList]);

  const activeListEntry = useMemo(() => listEntries.find((e) => e.isActive) ?? null, [listEntries]);
  const activeListLabel =
    activeListEntry?.label ?? activeCollection?.name ?? selectedCohortId ?? "Select a list";

  // ==============================================================================
  // SYNC EFFECT: Mirror reader cohort studies into local state so feedback updates
  // and direct URL fetches can mutate entries without losing reader-provided data.
  // ==============================================================================
  useEffect(() => {
    setStudies(sortStudies(readerStudies));
  }, [readerStudies]);

  // DEV-only: validate OP detection-cascade invariant once per cohort load.
  // Runs over raw readerData.studies (which carry detected_at_* fields), not
  // the transformed StudyData array (which drops them). Caps console output at
  // 20 warnings to avoid flooding the console on a broken cohort.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const studies = readerData?.studies;
    if (!studies?.length) return;
    const MAX_WARNINGS = 20;
    let emitted = 0;
    let total = 0;
    for (const study of studies) {
      if (isDecisionItem(study)) continue;
      const warnings = validateDetectionCascade(study);
      total += warnings.length;
      for (const w of warnings) {
        if (emitted < MAX_WARNINGS) {
          console.warn(w);
          emitted++;
        }
      }
    }
    if (total > MAX_WARNINGS) {
      console.warn(
        `[cascade] ...and ${total - MAX_WARNINGS} more cascade violation(s) across this cohort.`,
      );
    }
  }, [readerData]);

  // The worklist now lands in its full-width (expanded) form with no case
  // selected — the dominant stage is intentionally empty until the user clicks
  // a row, which collapses the list to the left and populates the preview. So
  // we no longer auto-select the first case on load. Clear any stale selection
  // when the cohort changes so a cohort switch returns to the expanded worklist.
  const clearedForCohortRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedCohortId) return;
    if (clearedForCohortRef.current === selectedCohortId) return;
    clearedForCohortRef.current = selectedCohortId;
    setPreviewStudyId(null);
  }, [selectedCohortId, setPreviewStudyId]);

  // Track when we're fetching a study directly from URL (not in current cohort)
  const [fetchingDirectStudyId, setFetchingDirectStudyId] = useState<string | null>(
    viewMode === "case" || viewMode === "reporter" ? (studyId ?? null) : null,
  );

  // Direct study loading for URL navigation. When a user deep-links to a study
  // outside the current cohort, add a stub StudyData so CaseViewer renders;
  // CaseViewer fetches its own predictions/metadata via useModelPrediction.
  useEffect(() => {
    if (!studyId || !token || !userId) return;
    const existingStudy = studies.find((s) => s.studyId === studyId);
    if (existingStudy) {
      if (fetchingDirectStudyId === studyId) setFetchingDirectStudyId(null);
      return;
    }
    if (isLoading) return;

    setFetchingDirectStudyId(studyId);
    setStudies((prev) => {
      if (prev.some((s) => s.studyId === studyId)) return prev;
      return [
        ...prev,
        {
          studyId,
          series: [],
          isLoaded: true,
          isLoading: false,
          predictions: {},
          activeModelType: "experimental" as const,
          report: "",
          metadata: {},
        },
      ];
    });
    setFetchingDirectStudyId(null);
  }, [studyId, token, userId, isLoading, studies, fetchingDirectStudyId]);

  const [exportingReports, setExportingReports] = useState(false);
  const [exportReportsError, setExportReportsError] = useState<string | null>(null);
  const exportAllReports = useCallback(async () => {
    if (!token || !userId) return;
    const completed = allFilteredStudies.filter(
      (study) =>
        !isDecisionDevice(study) &&
        !isModelRunning(study.inferenceStatus) &&
        !isModelFailed(study.inferenceStatus),
    );
    if (!completed.length) {
      setExportReportsError(
        "No completed cases in the current worklist view have reports to export.",
      );
      return;
    }
    setExportingReports(true);
    setExportReportsError(null);
    try {
      const reports = [];
      // Report generation is bounded so a large worklist does not fan out all
      // of its requests at once.
      for (let index = 0; index < completed.length; index += 4) {
        const results = await Promise.all(
          completed.slice(index, index + 4).map(async (study) => {
            const predictionRequest = fetchModelPrediction(token, userId, study.studyId)
              .then((prediction) => {
                if (!prediction) throw new Error(ACCESSION_LOOKUP_FAILED);
                return prediction;
              })
              .catch(() => {
                throw new Error(ACCESSION_LOOKUP_FAILED);
              });
            const [saved, generated, prediction] = await Promise.all([
              fetchReportState(token, userId, study.studyId).catch(() => null),
              fetchReport(study.studyId, token).catch(() => null),
              predictionRequest,
            ]);
            // "note" keeps the rest of the export downloadable: a report whose
            // Impression cannot be generated states that in its own PDF pages.
            // A case with no report data at all resolves to null and is
            // counted in the shortfall message, never silently dropped.
            return makeDownloadableReport(study.studyId, saved ?? {}, generated, {
              accessionNumber: prediction.accessionNumber,
              onImpressionFailure: "note",
            }).catch(() => null);
          }),
        );
        reports.push(...results.filter((report) => report !== null));
      }
      if (!reports.length) {
        setExportReportsError("Reports are not available yet. Try again after analysis completes.");
        return;
      }
      const date = new Date().toISOString().slice(0, 10);
      await downloadCombinedReports(reports, `worklist-reports-${date}.pdf`);
      setExportReportsError(summarizeExportShortfall(completed.length, reports));
    } catch (error) {
      setExportReportsError(
        error instanceof Error && error.message === ACCESSION_LOOKUP_FAILED
          ? "Could not verify accession numbers. No PDF was downloaded. Try again."
          : "Could not export the worklist reports. No PDF was downloaded.",
      );
    } finally {
      setExportingReports(false);
    }
  }, [allFilteredStudies, token, userId]);

  // Unified viewer mode handling (case/reporter)
  if ((viewMode === "case" || viewMode === "reporter") && studyId) {
    const selectedStudy =
      readerStudies.find((s) => s.studyId === studyId) ??
      studies.find((s) => s.studyId === studyId);
    // In a multi-cohort collection, the collection's primary cohort is only a
    // list-loading hint. Comparison data belongs to the selected study's own
    // reader cohort. The explicit route cohort bridges the initial render until
    // the merged study index has loaded.
    const caseReaderCohortId = studyIndex[studyId]?.cohortId || cohortFromRoute || selectedCohortId;

    // Auth check
    if (!token || !userId) {
      navigate("/login");
      return null;
    }

    // Genuine "study not found": the cohort has resolved, no direct fetch is in
    // flight, and the study isn't in it. While loading or fetching we fall
    // through and render the viewer shell (below) instead of a separate screen.
    if (!selectedStudy && !isLoading && fetchingDirectStudyId !== studyId) {
      return (
        <div className="flex h-screen bg-dark text-white items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-medium mb-4">Study not found</h2>
            <button
              onClick={() => navigate("/worklist")}
              className="px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg transition-colors"
            >
              Return to Worklist
            </button>
          </div>
        </div>
      );
    }

    // Pass study/model identity and geometry hints when the cohort row is ready.
    // CaseViewer loads metadata, predictions, and reports by studyId, so a route
    // identity alone can render its shell while the cohort loads. The stable key
    // keeps the same viewer mounted when the row and its hints arrive.
    const studyDataProps = selectedStudy
      ? {
          studyId: selectedStudy.studyId,
          modelSelectedSeriesId: selectedStudy.selectedSeriesId,
          analyzedSeriesId: selectedStudy.analyzedSeriesId,
          device_id: selectedStudy.device_id,
          triageDevice: selectedStudy.triageDevice,
        }
      : { studyId };

    // Render CaseViewer for both 'case' and 'reporter' modes
    // (Reporter.tsx was removed - CaseViewer now handles all viewing/reporting)
    return (
      <CaseViewer
        key={studyId}
        studyData={studyDataProps}
        reviewCohortId={selectedCohortId ?? undefined}
        readerCohortId={caseReaderCohortId ?? undefined}
        enableSegmentation={(() => {
          const ids = (import.meta.env.VITE_SEGMENTATION_COHORT_IDS ?? "").trim();
          if (ids === "*") return true;
          return ids
            .split(",")
            .map((s: string) => s.trim().toLowerCase())
            .filter(Boolean)
            .includes(selectedCohortId?.toLowerCase() ?? "");
        })()}
      />
    );
  }

  const needsListSelection =
    !isReviewMode && !selectedCohortId && selectedCollectionId !== "__saved__";
  const unresolvedCollection =
    !isReviewMode &&
    !!selectedCollectionId &&
    selectedCollectionId !== "__saved__" &&
    !activeCollection;
  const needsCollectionConfig = needsListSelection || unresolvedCollection;
  const loadingListSelection =
    needsCollectionConfig && (collectionsLoading || panelsLoading || !!soleExternalList);
  const listSelectionError = needsCollectionConfig && collectionsError;

  if (needsCollectionConfig) {
    return (
      <WorklistListPicker
        entries={listEntries}
        loading={loadingListSelection}
        error={listSelectionError}
        unavailable={unresolvedCollection}
        onRetry={() => {
          void retryCollections();
        }}
        panelsFailed={panelsError}
        onRetryPanels={() => {
          void retryPanels();
        }}
      />
    );
  }

  // Shared case-list body (loading / empty / rows). Rows are thin selectable
  // entries: clicking one loads its gallery in the dominant right-pane stage.
  const renderCaseList = () => (
    <div className="divide-y divide-border">
      {isLoading ? (
        <GhostWorklistRows count={12} />
      ) : isCohortError && filteredStudies.length === 0 ? (
        // Honest backend-failure state — NOT "no studies match filters" (which
        // would falsely blame a filter and offer a clear-filters action that
        // can't help). The cohort/reader query errored (unreachable / 5xx).
        <div className="px-4 py-16 text-center">
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded bg-warn-tint flex items-center justify-center mb-4">
              <span className="text-warn text-xl font-semibold">!</span>
            </div>
            <p className="text-fg-muted mb-1">Couldn't load this list</p>
            <p className="text-fg-faint text-xs mb-3">The service is unavailable or unreachable.</p>
            <button
              onClick={() => refetchCohort()}
              className="rounded-sharp border border-border-strong px-3 py-1 text-sm text-signal-text-tint hover:text-fg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      ) : filteredStudies.length === 0 ? (
        (() => {
          const hasActiveFilters =
            selectedFindings.length > 0 || !!selectedDeviceId || searchQuery.trim().length > 0;
          return (
            <div className="px-4 py-16 text-center">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded bg-surface-2 flex items-center justify-center mb-4">
                  <FaFilter className="w-6 h-6 text-fg-muted" />
                </div>
                {hasActiveFilters ? (
                  <>
                    <p className="text-fg-muted mb-2">No studies match the current filters</p>
                    <button
                      onClick={() => {
                        setSelectedDeviceId(null);
                        clearFindingFilters();
                        setSearchQuery("");
                        setSortMode("default");
                      }}
                      className="text-sm text-signal-text-tint hover:text-signal-text-tint transition-colors"
                    >
                      Clear all filters
                    </button>
                  </>
                ) : !showCompleted && hiddenCount > 0 ? (
                  // Honest empty state: the list isn't empty, every case has just
                  // been reviewed and/or skipped and hidden. Offer the un-hide.
                  <>
                    <p className="text-fg-muted mb-2">{emptyLabel}</p>
                    <button
                      onClick={() => setShowCompleted(true)}
                      className="text-sm text-signal-text-tint hover:text-signal-text-tint transition-colors"
                    >
                      {showToggleLabel}
                    </button>
                  </>
                ) : (
                  <p className="text-fg-muted">No cases in this list</p>
                )}
              </div>
            </div>
          );
        })()
      ) : !previewOpen ? (
        // EXPERIMENTAL — the full-width worklist as a table (AI Results ·
        // Severity · Status · Exam · Case). The collapsed preview falls through
        // to the thin CaseRow list below.
        <div>
          <WorklistTableHeader />
          {filteredStudies.map((study) => {
            const bareUid = study.studyId.split("::")[0];
            return (
              <motion.div
                key={study.studyId}
                layout="position"
                transition={
                  animateReorder
                    ? { type: "spring", stiffness: 450, damping: 40, mass: 0.9 }
                    : { duration: 0 }
                }
              >
                <CaseTableRow
                  study={study}
                  isActive={previewStudyId === study.studyId}
                  onSelect={handleSelectStudy}
                  severity={studyIndex[study.studyId]?.severity ?? null}
                  severitySource={studyIndex[study.studyId]?.severitySource}
                  isSaved={savedCaseSet.has(study.studyId)}
                  onToggleSave={handleToggleSave}
                  cohortId={selectedCohortId ?? undefined}
                  disposition={
                    isReviewMode ? (dispositionByStudyUid.get(bareUid) ?? null) : undefined
                  }
                  isReviewed={studyIndex[study.studyId]?.surfacerReviewed ?? false}
                  isSkipped={studyIndex[study.studyId]?.skipped ?? false}
                  skipReason={studyIndex[study.studyId]?.skipReason}
                  skipNote={studyIndex[study.studyId]?.skipNote}
                  onContextMenu={
                    !isReviewMode && studyIndex[study.studyId]?.isReporter
                      ? handleRowContextMenu
                      : undefined
                  }
                />
              </motion.div>
            );
          })}
        </div>
      ) : (
        filteredStudies.map((study) => {
          const bareUid = study.studyId.split("::")[0];
          return (
            // `layout="position"` makes each row FLIP-animate to its new slot
            // when the order changes — so switching to Severity / Most findings
            // visibly slides cases into their new rank. Layout tracking is
            // always on (keyed by studyId), but the transition is instant for
            // the non-animated sorts, so only those two animate. Position only
            // (not size) avoids height-change distortion.
            <motion.div
              key={study.studyId}
              layout="position"
              transition={
                animateReorder
                  ? { type: "spring", stiffness: 450, damping: 40, mass: 0.9 }
                  : { duration: 0 }
              }
            >
              <CaseRow
                study={study}
                isSaved={savedCaseSet.has(study.studyId)}
                onToggleSave={handleToggleSave}
                cohortId={selectedCohortId ?? undefined}
                disposition={
                  isReviewMode ? (dispositionByStudyUid.get(bareUid) ?? null) : undefined
                }
                isActive={previewStudyId === study.studyId}
                onSelect={handleSelectStudy}
                anchorTopRef={anchorTopRef}
                expanded={!previewOpen}
                severity={studyIndex[study.studyId]?.severity ?? null}
                severitySource={studyIndex[study.studyId]?.severitySource}
                isReviewed={studyIndex[study.studyId]?.surfacerReviewed ?? false}
                isSkipped={studyIndex[study.studyId]?.skipped ?? false}
                skipReason={studyIndex[study.studyId]?.skipReason}
                skipNote={studyIndex[study.studyId]?.skipNote}
                // Skip is surfacer-only: only reporter rows join skippedByStudy and
                // get a badge / fall off. Gate the menu at the entry point too (not
                // just the display join) so a cortex/drafter row in a mixed list
                // can't write a stranded, invisible skip — and keeps its native
                // context menu. The index is in scope here, so gate on isReporter.
                onContextMenu={
                  !isReviewMode && studyIndex[study.studyId]?.isReporter
                    ? handleRowContextMenu
                    : undefined
                }
              />
            </motion.div>
          );
        })
      )}
    </div>
  );

  const paginationControls = totalStudies > 0 && (
    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-bg-2 border-t border-premium-subtle shrink-0 font-mono text-[11px] text-fg-dim">
      <span className="whitespace-nowrap">
        <span className="text-fg-muted">
          {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, totalStudies)}
        </span>{" "}
        / {totalStudies}
      </span>
      {totalPages > 1 && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="rounded-sharp border border-border-strong px-2 py-0.5 text-fg-muted hover:text-fg disabled:opacity-40"
          >
            Prev
          </button>
          <span className="whitespace-nowrap text-fg-muted">
            {currentPage + 1}/{totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="rounded-sharp border border-border-strong px-2 py-0.5 text-fg-muted hover:text-fg disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  // ============================================================================
  // WORKSTATION LAYOUT — shared by list and review modes. Fixed-height frame: a
  // top bar (cohort/list switcher + search + sort), a thin scrollable case list
  // (the only scroll region), and a dominant persistent stage showing the
  // selected case's PreviewGallery.
  // ============================================================================
  // Plain computation, not a hook: this runs after the viewer-mode early return.
  const hasTriageRows = Object.values(studyIndex).some((entry) => entry.triageDevice);

  const sortControl = (
    <div className="flex items-center gap-1.5">
      <FaSortAmountDown className="w-3 h-3 text-fg-muted" />
      {/* `appearance-none` strips the platform-native control chrome (Safari
          otherwise renders a beveled 2004-style button); we re-add our own
          chevron so the select looks identical on Safari, Chrome, and Firefox. */}
      <div className="relative">
        <select
          value={sortMode}
          onChange={(e) => {
            setSortMode(e.target.value as SortMode);
            setCurrentPage(0);
          }}
          className="appearance-none text-xs bg-surface-2 border border-border-strong rounded-xs pl-1.5 pr-6 py-1 text-fg-muted cursor-pointer focus:outline-none focus:border-border-strong"
        >
          <option value="default">Default</option>
          <option value="severity">Severity</option>
          <option value="findings_desc">Most findings</option>
          <option value="findings_asc">Fewest findings</option>
          <option value="alphabetical">Alphabetical</option>
          {hasTriageRows && <option value="flagged">Flagged first</option>}
        </select>
        <FaChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-fg-muted" />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0 text-fg bg-bg page-premium-alt overflow-hidden">
      {/* Top bar — list switcher + search + sort. Brand/Admin/Sign-out live
            in the app Navbar one row up, so they're intentionally not repeated. */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-border bg-bg-2 shrink-0">
        {/* List-as-count switcher */}
        <div className="relative">
          <button
            onClick={() => setListSwitcherOpen((o) => !o)}
            className="flex items-center gap-3 px-3 py-1.5 border border-border-strong rounded-xs hover:border-fg-dim transition-colors"
          >
            <span className="text-[8.5px] font-mono uppercase tracking-[0.18em] text-fg-dim">
              {isReviewMode ? "Cohort" : "List"}
            </span>
            <span className="text-sm font-semibold tracking-tight text-fg max-w-[18rem] truncate">
              {activeListLabel}
            </span>
            <span className="text-xs font-mono font-semibold text-signal-text-tint bg-signal-tint px-2 py-0.5 rounded-xs tabular-nums">
              {/* On a load failure the count is unknown, not zero — "0" would
                    falsely read as an empty list, contradicting the error state. */}
              {isCohortError || isLoading ? "—" : totalStudies}
            </span>
            <FaChevronDown className="w-2.5 h-2.5 text-fg-faint" />
          </button>
          {listSwitcherOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setListSwitcherOpen(false)} />
              <div className="absolute left-0 mt-1.5 z-30 w-80 max-h-[60vh] overflow-y-auto custom-scrollbar bg-bg-2 border border-border-strong rounded-xs shadow-xl shadow-black/40 py-1">
                <div className="px-3 py-2 text-[8.5px] font-mono uppercase tracking-[0.18em] text-fg-dim">
                  {isReviewMode ? "Switch cohort" : "Switch list"}
                </div>
                {listEntries.map((entry) => (
                  <WorklistListOption
                    key={entry.key}
                    entry={entry}
                    compact
                    onSelect={() => {
                      entry.onSelect();
                      setListSwitcherOpen(false);
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex-1" />

        {allFilteredStudies.some((study) => !isDecisionDevice(study)) && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={exportAllReports}
              disabled={exportingReports}
              data-testid="worklist-export-reports-button"
              className="flex items-center gap-1 rounded-xs border border-border-strong px-2 py-1 font-mono text-[9px] text-fg-muted transition-colors hover:text-fg disabled:cursor-wait disabled:opacity-60"
              title="Export reports for all completed cases in the current worklist view as PDF"
            >
              <FaDownload className="h-2.5 w-2.5" />
              {exportingReports ? "Preparing reports…" : "Export reports"}
            </button>
            {exportReportsError && (
              <span
                role="alert"
                className="absolute right-0 top-full z-30 mt-1 w-64 rounded-xs border border-destructive/50 bg-bg-2 p-2 text-[10px] text-destructive shadow-xl"
              >
                {exportReportsError}
              </span>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative w-64">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-fg-muted pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(0);
            }}
            placeholder="Search finding or study ID…"
            className="w-full pl-8 pr-7 py-1.5 text-xs bg-surface-2 border border-border-strong rounded-xs text-fg placeholder-fg-dim focus:outline-none focus:border-primary/50 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setCurrentPage(0);
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg"
            >
              <FaTimes className="w-3 h-3" />
            </button>
          )}
        </div>

        {sortControl}

        {isLoading && (
          <span className="text-xs text-fg-muted flex items-center gap-1 shrink-0">
            <span className="w-2 h-2 border border-border-strong border-t-primary rounded-full animate-spin" />
            Indexing…
          </span>
        )}
      </div>

      {/* Subcohort (source-delivery batch) filter. One worklist, scoped to a
          delivery batch. Hidden unless the cohort carries batches (e.g. Unilabs
          cal46/emo32/ae_abdomen/...). Reviewers walk their scoped set, so it's
          workstation-only like the findings filter. */}
      {!isReviewMode && batchNames.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-border shrink-0">
          <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-fg-dim mr-1">
            Subcohort
          </span>
          <button
            onClick={() => {
              setSelectedBatch(null);
              setCurrentPage(0);
            }}
            className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-xs border transition-colors ${
              selectedBatch === null
                ? "bg-signal-tint text-signal-text-tint border-signal-stroke"
                : "bg-surface-2 text-fg-muted border-border hover:text-fg"
            }`}
          >
            all <span className="tabular-nums">{isCohortError ? "—" : cohortStudyCount}</span>
          </button>
          {batchNames.map((b) => (
            <button
              key={b}
              onClick={() => {
                setSelectedBatch((cur) => (cur === b ? null : b));
                setCurrentPage(0);
              }}
              className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-xs border transition-colors ${
                selectedBatch === b
                  ? "bg-signal-tint text-signal-text-tint border-signal-stroke"
                  : "bg-surface-2 text-fg-muted border-border hover:text-fg"
              }`}
            >
              {b} <span className="tabular-nums">{batchCounts[b]}</span>
            </button>
          ))}
        </div>
      )}

      {/* Body — full-width worklist (preview closed) OR thin list + dominant
          stage (preview open). A flex row whose list panel animates its WIDTH,
          so the panel's right edge glides smoothly between full-width and the
          320px preview column (grid-template-columns can't interpolate fr<->px,
          which read as a jump). */}
      <div className="flex flex-1 min-h-0">
        {/* Case list — full width when expanded, thin column when previewing. */}
        <motion.div
          initial={false}
          animate={{ width: previewOpen ? 320 : "100%" }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
          className={`flex flex-col min-h-0 shrink-0 overflow-hidden ${previewOpen ? "border-r border-border" : ""}`}
        >
          {/* List header — compact findings-filter affordance. */}
          <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-border shrink-0 relative">
            <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-fg-dim">
              Cases
            </span>
            {/* Reviewers walk their scoped cohort top-to-bottom; the
                  findings/model filter is intentionally workstation-only. */}
            {!isReviewMode && (
              <div className="flex items-center gap-1.5">
                {/* Surfacer "fall off once reviewed" + skipped cases — both are
                    hidden by default; this one toggle reveals them. Only shown
                    when there is something to reveal (or while it's on, so it can
                    be turned off). The count makes the hide observable. */}
                {hasReporterRows && (
                  <>
                    <button
                      onClick={() => setOnlyUnjudged(!onlyUnjudged)}
                      aria-pressed={onlyUnjudged}
                      className={`flex items-center gap-1 text-[9px] font-mono px-2 py-1 rounded-xs border transition-colors ${
                        onlyUnjudged
                          ? "border-signal-stroke text-signal-text-tint"
                          : "border-border-strong text-fg-muted hover:text-fg"
                      }`}
                      title={
                        onlyUnjudged
                          ? "Hiding cases where every finding has a verdict from someone, in any version. Click to go by your own verdicts only."
                          : "Also hide cases someone has already judged, in any version"
                      }
                      data-testid="toggle-only-unjudged"
                    >
                      Only unjudged
                    </button>
                    {unjudgedLeft && (
                      <span
                        className="text-[9px] font-mono text-fg-dim whitespace-nowrap"
                        title={`Findings nobody has judged, across ${unjudgedLeft.cases} case${
                          unjudgedLeft.cases === 1 ? "" : "s"
                        }`}
                        data-testid="worklist-unjudged-left"
                      >
                        {unjudgedLeft.findings} unjudged left
                      </span>
                    )}
                  </>
                )}
                {(hiddenCount > 0 || showCompleted) && (
                  <button
                    onClick={() => setShowCompleted((v) => !v)}
                    className={`flex items-center gap-1 text-[9px] font-mono px-2 py-1 rounded-xs border transition-colors ${
                      showCompleted
                        ? "border-signal-stroke text-signal-text-tint"
                        : "border-border-strong text-fg-muted hover:text-fg"
                    }`}
                    title={
                      showCompleted
                        ? "Hide reviewed, judged and skipped cases"
                        : "Show reviewed, judged and skipped cases"
                    }
                    data-testid="toggle-show-completed"
                  >
                    {showCompleted ? hideToggleLabel : showToggleLabel}
                  </button>
                )}
                {selectedFindings.length > 0 && (
                  <button
                    onClick={clearFindingFilters}
                    className="flex items-center gap-1 text-[9px] font-mono text-signal-text-tint border border-signal-stroke px-2 py-1 rounded-xs hover:bg-signal-tint transition-colors"
                    title="Clear finding filter"
                  >
                    {selectedFindings.length} finding{selectedFindings.length > 1 ? "s" : ""}
                    <FaTimes className="w-2 h-2" />
                  </button>
                )}
                <button
                  onClick={() => setFindingsPopoverOpen((o) => !o)}
                  className={`flex items-center gap-1 text-[9px] font-mono px-2 py-1 rounded-xs border transition-colors ${
                    findingsPopoverOpen || selectedFindings.length > 0
                      ? "border-signal-stroke text-signal-text-tint"
                      : "border-border-strong text-fg-muted hover:text-fg"
                  }`}
                  title="Filter findings"
                >
                  <FaFilter className="w-2.5 h-2.5" />
                  Filter
                </button>
              </div>
            )}

            {/* Findings + model filter popover — same state/handlers as the
                  old sidebar Filters section, just relocated. */}
            {findingsPopoverOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setFindingsPopoverOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-30 w-72 max-h-[70vh] overflow-y-auto custom-scrollbar bg-bg-2 border border-border-strong rounded-xs shadow-xl shadow-black/40 p-3 space-y-3">
                  {/* Model filter — only when more than one device present. */}
                  {availableDevices.length > 1 && (
                    <div className="filter-section">
                      <div className="filter-section-header">
                        <span className="filter-section-title">Model</span>
                        {selectedDeviceId && (
                          <button
                            onClick={() => setSelectedDeviceId(null)}
                            className="text-[10px] text-fg-muted hover:text-fg transition-colors"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      <RadioGroup
                        value={selectedDeviceId ?? "all"}
                        onValueChange={(value) =>
                          setSelectedDeviceId(value === "all" ? null : value)
                        }
                        className="space-y-0.5"
                      >
                        <Label
                          htmlFor="device-all"
                          className={`filter-item ${!selectedDeviceId ? "filter-item-active" : ""}`}
                        >
                          <RadioGroupItem value="all" id="device-all" className="sr-only" />
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${!selectedDeviceId ? "bg-primary" : "bg-border-strong"}`}
                          />
                          <span className="flex-1 text-xs">All Models</span>
                          <span
                            className={`filter-count ${!selectedDeviceId ? "filter-count-active" : ""}`}
                          >
                            {Object.keys(studyIndex).length || studies.length}
                          </span>
                        </Label>
                        {availableDevices.map((device) => {
                          const isSelected = selectedDeviceId === device.device_id;
                          return (
                            <Label
                              key={device.device_id}
                              htmlFor={`device-${device.device_id}`}
                              className={`filter-item ${isSelected ? "filter-item-active" : ""}`}
                            >
                              <RadioGroupItem
                                value={device.device_id}
                                id={`device-${device.device_id}`}
                                className="sr-only"
                              />
                              <span
                                className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSelected ? "bg-primary" : "bg-border-strong"}`}
                              />
                              <span
                                className="flex-1 text-[11px] font-mono min-w-0"
                                title={device.display_name}
                              >
                                {middleEllipsis(device.display_name, 22)}
                              </span>
                              <span
                                className={`filter-count ${isSelected ? "filter-count-active" : ""}`}
                              >
                                {device.study_count}
                              </span>
                            </Label>
                          );
                        })}
                      </RadioGroup>
                    </div>
                  )}

                  {/* Findings filter */}
                  <div className="filter-section">
                    <div className="filter-section-header">
                      <span className="filter-section-title">Findings</span>
                      {selectedFindings.length > 0 && (
                        <button
                          onClick={clearFindingFilters}
                          className="text-[10px] text-signal-text-tint hover:text-signal-text-tint transition-colors"
                        >
                          Clear ({selectedFindings.length})
                        </button>
                      )}
                    </div>

                    {selectedFindings.length > 1 && (
                      <div className="flex items-center gap-0.5 mb-2 p-0.5 bg-bg rounded-xs">
                        <button
                          onClick={() => setFindingFilterMode("any")}
                          className={`flex-1 px-2 py-1 text-[10px] font-medium rounded-xs transition-all ${
                            findingFilterMode === "any"
                              ? "bg-primary/80 text-white"
                              : "text-fg-muted hover:text-fg"
                          }`}
                        >
                          Match Any
                        </button>
                        <button
                          onClick={() => setFindingFilterMode("all")}
                          className={`flex-1 px-2 py-1 text-[10px] font-medium rounded-xs transition-all ${
                            findingFilterMode === "all"
                              ? "bg-primary/80 text-white"
                              : "text-fg-muted hover:text-fg"
                          }`}
                        >
                          Match All
                        </button>
                      </div>
                    )}

                    {allUniqueFindingsWithCounts.length > 8 && (
                      <div className="relative mb-1.5">
                        <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-fg-muted pointer-events-none" />
                        <input
                          type="text"
                          value={findingSearch}
                          onChange={(e) => setFindingSearch(e.target.value)}
                          placeholder="Filter findings..."
                          className="w-full pl-7 pr-6 py-1 text-[10px] bg-bg border border-border rounded-xs text-fg-muted placeholder-fg-faint focus:outline-none focus:border-border-strong"
                        />
                        {findingSearch && (
                          <button
                            onClick={() => setFindingSearch("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg"
                          >
                            <FaTimes className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    )}
                    <div className="space-y-0.5 max-h-[40vh] overflow-y-auto custom-scrollbar">
                      {allUniqueFindingsWithCounts.length === 0 ? (
                        <p className="text-[10px] text-fg-muted italic py-3 text-center">
                          {isLoading ? "Loading..." : "No findings"}
                        </p>
                      ) : (
                        allUniqueFindingsWithCounts
                          .filter(({ finding }) => {
                            if (!findingSearch) return true;
                            return finding.toLowerCase().includes(findingSearch.toLowerCase());
                          })
                          .map(({ finding, count, label: findingName }) => {
                            const isSelected = selectedFindings.includes(finding);
                            return (
                              <Label
                                key={finding}
                                htmlFor={`finding-${finding}`}
                                data-testid="finding-filter-item"
                                data-finding-name={findingName}
                                className={`filter-item ${isSelected ? "filter-item-active" : ""}`}
                              >
                                <Checkbox
                                  id={`finding-${finding}`}
                                  checked={isSelected}
                                  onCheckedChange={() => toggleFindingFilter(finding)}
                                  className="h-3 w-3"
                                />
                                <span className="flex-1 text-[11px] truncate">
                                  {formatFindingName(findingName)}
                                </span>
                                <span
                                  className={`filter-count ${isSelected ? "filter-count-active" : ""}`}
                                >
                                  {count}
                                </span>
                              </Label>
                            );
                          })
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Rows — the only scroll region. */}
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">{renderCaseList()}</div>

          {paginationControls}
        </motion.div>

        {/* Dominant persistent stage — selected case's PreviewGallery. The
              host is a bounded flex column (flex-1 min-h-0) so the gallery's
              ResizeObserver measures real height (the fix that makes the gallery
              render). PreviewGallery owns its own header (study id, meta,
              non-diagnostic disclaimer, Open report), so the stage host adds no
              chrome of its own — `meta` + `onOpenReport` are threaded down.
              `overflow-hidden` clips the stage to zero width while the worklist
              is expanded (the list panel's width animates to 100%, leaving the
              stage no room). */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 bg-bg overflow-hidden">
          {previewStudy && previewStudy.device_id && !previewStudy.triageDevice ? (
            <div className="flex-1 min-h-0 px-4 py-3 flex flex-col">
              {/* Reporter/surfacer cohorts (comparison_counts shape) get the
                    retro agreement-on-image stage; cortex/drafter keep the gallery. */}
              {isReporterStudy(previewStudy) ? (
                <RetroAgreementStage
                  key={previewStudy.studyId}
                  studyId={previewStudy.studyId}
                  deviceId={previewStudy.device_id}
                  cohortId={studyIndex[previewStudy.studyId]?.cohortId || selectedCohortId}
                  meta={previewStageMeta}
                  onOpenReport={() => handleOpenFullViewer(previewStudy.studyId)}
                />
              ) : (
                <ActiveCaseStage
                  key={previewStudy.studyId}
                  studyId={previewStudy.studyId}
                  deviceId={previewStudy.device_id}
                  passing={studyIndex[previewStudy.studyId]?.findingsSet ?? EMPTY_PASSING_SET}
                  meta={previewStageMeta}
                  onOpenReport={() => handleOpenFullViewer(previewStudy.studyId)}
                />
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-fg-dim text-sm">
              {/* Mirror the list's unavailable state — don't invite selecting a
                    case when the service is down and nothing is loadable. */}
              {isLoading
                ? "Loading…"
                : isCohortError
                  ? "Service unavailable"
                  : "Select a case to preview"}
            </div>
          )}
        </div>
      </div>

      {/* Skip context-menu — right-click a row to set aside a case (or undo).
          A portal, so it floats above the worklist at the click anchor. */}
      {skipMenu &&
        (() => {
          const info = skippedByStudy[skipMenu.studyUid];
          // Record the study's true cohort (precise in merged multi-cohort
          // views), falling back to the selected cohort if the row is missing
          // from the index. No cohort at all ⇒ don't open the menu.
          const cohortId = studyIndex[skipMenu.studyId]?.cohortId || selectedCohortId;
          if (!cohortId) return null;
          const studyUid = skipMenu.studyUid;
          return (
            <SkipReasonMenu
              anchor={{ x: skipMenu.x, y: skipMenu.y }}
              isSkipped={!!info}
              currentReason={info?.reason}
              currentNote={info?.reason_note}
              onSkip={(reason: SkipReason, note?: string) =>
                setSkipped({ cohortId, studyUid, skipped: true, reason, reasonNote: note })
              }
              onUnskip={() => setSkipped({ cohortId, studyUid, skipped: false })}
              onClose={() => setSkipMenu(null)}
            />
          );
        })()}
    </div>
  );
};

export default Worklist;
